
import React, { useEffect, useRef, useState } from 'react';
import { LineStyle, LineSeries, BaselineSeries, createSeriesMarkers } from 'lightweight-charts';
import { EMA, Extremums, Pivot, ATR } from '@debut/indicators';
import { convertCandles, intervalToMinutes } from '#src/utils'
import { TrendLine } from './plugins/TrendLine';
import { VertLine } from './plugins/VertLine';
import { kmeans } from 'ml-kmeans';
import dbscan from '@cdxoo/dbscan'

import { BacktestSettings } from '#state/settings';

// ***********************************************
// view config
// ***********************************************

// filter 2
const similarLevelPercent = 0.002
// 
const simpleStatsEmaLength = 21
const waveDurationMult = 7 * 24 * 60 * 60 // 1 week in seconds
// 
const fiboHL = [0, 1]
const fiboCorrectionLevels = [0.236, 0.382, 0.5, 0.618, 0.786] // 0.236, 0.786, 1.618, 2, 2.618, 4.236]
const fiboUpExtension = [1.618, -0.236, -0.382, -0.5, -0.618, -0.786, -1, -1.236, -1.618]
const fiboDownExtension = [-0.618, 1.236, 1.382, 1.5, 1.618, 1.786, 2, 2.618]


// ***********************************************
// draw
// ***********************************************

export function drawClusterExpectancy(chart, opts) {
  const {
    clusterExpectancy,
    optimalWaves,
  } = opts

  const series = []


  const seriesData = [];
  const seriesExp = chart.addSeries(LineSeries, {
    color: 'transparent',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesExp)


  const baseOpacityMult = 3
  const zoneOpacityMult = 2
  const stopOpacityMult = 3
  // *******************************

  const lastWave = optimalWaves[optimalWaves.length - 1];
  const prevWave = optimalWaves[optimalWaves.length - 2];
  const prePrevWave = optimalWaves[optimalWaves.length - 3];

  const lastDeltaTime = lastWave.end - lastWave.start
  const nextEndTime = lastWave.end + lastDeltaTime

  seriesData.push({ time: prePrevWave.start, value: prePrevWave.high })
  seriesData.push({ time: prevWave.end, value: prevWave.high })
  seriesData.push({ time: lastWave.end, value: lastWave.high })
  seriesData.push({ time: nextEndTime, value: lastWave.high })

  seriesData.sort((a, b) => a.time - b.time)



  function handleTrends(series, clusters, prevWave, lastWave, nextEndTime, isMove = false, adjustEntryPrice = false) {
    const target1Color = isMove ? '255, 153, 0' : '50, 180, 255'
    const target2Color = isMove ? '242, 255, 0' : '0, 225, 150'
    // const color1 = 'rgb(242, 255, 0)'

    for (let i = 0; i < clusters.length; i++) {
      const cluster = clusters[i]
      const initPrice = prevWave.direction === 'up'
        ? prevWave.high
        : prevWave.low

      const enterPrice = adjustEntryPrice
        ? lastWave.direction === 'up'
          ? lastWave.high
          : lastWave.low
        : prevWave.direction === 'up'
          ? initPrice - initPrice * cluster.enter
          : initPrice + initPrice * cluster.enter

      const target1Price = prevWave.direction === 'up'
        ? enterPrice + enterPrice * cluster.target1
        : enterPrice - enterPrice * cluster.target1

      const target2Price = prevWave.direction === 'up'
        ? enterPrice + enterPrice * cluster.target2
        : enterPrice - enterPrice * cluster.target2

      const slPrice = prevWave.direction === 'up'
        ? enterPrice - enterPrice * cluster.stopLoss
        : enterPrice + enterPrice * cluster.stopLoss

      const tslPrice = prevWave.direction === 'up'
        ? enterPrice - enterPrice * cluster.trailingSL
        : enterPrice + enterPrice * cluster.trailingSL

      const pointStart = { time: lastWave.end, price: enterPrice }

      // Target 1
      const dataT1 = cluster.cluster.outcomes.pullback
      const pointEnd1 = { time: nextEndTime, price: target1Price }

      const pointStart1L = { time: lastWave.end, price: enterPrice - cluster.enterDev }
      const pointEnd1L = { time: nextEndTime, price: target1Price - cluster.target1Dev }

      const pointStart1H = { time: lastWave.end, price: enterPrice + cluster.enterDev }
      const pointEnd1H = { time: nextEndTime, price: target1Price + cluster.target1Dev }

      const pointStart1HL = { time: nextEndTime, price: target1Price - cluster.target1Dev }
      const pointEnd1HL = { time: nextEndTime, price: target1Price + cluster.target1Dev }


      const trendT1 = new TrendLine(chart, series, pointStart, pointEnd1, {
        lineColor: `rgba(${target1Color}, ${Math.min(cluster.prob * dataT1.prob * baseOpacityMult, 1)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT1);

      const trendT1L = new TrendLine(chart, series, pointStart1L, pointEnd1L, {
        lineColor: `rgba(${target1Color}, ${Math.min(cluster.prob * dataT1.prob * zoneOpacityMult, 0.5)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT1L);

      const trendT1H = new TrendLine(chart, series, pointStart1H, pointEnd1H, {
        lineColor: `rgba(${target1Color}, ${Math.min(cluster.prob * dataT1.prob * zoneOpacityMult, 0.5)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT1H);

      // const trendT1HL = new TrendLine(chart, series, pointStart1HL, pointEnd1HL, {
      //   lineColor: `rgba(${target1Color}, ${Math.min(cluster.prob * dataT1.prob * zoneOpacityMult, 0.5)})`,
      //   width: 2,
      //   showLabels: false,
      // });
      // series.attachPrimitive(trendT1HL);

      // Target 2 
      const dataT2 = cluster.cluster.outcomes.move
      const pointEnd2 = { time: nextEndTime, price: target2Price }

      const pointStart2L = { time: lastWave.end, price: enterPrice - cluster.enterDev }
      const pointEnd2L = { time: nextEndTime, price: target2Price - cluster.target2Dev }

      const pointStart2H = { time: lastWave.end, price: enterPrice + cluster.enterDev }
      const pointEnd2H = { time: nextEndTime, price: target2Price + cluster.target2Dev }

      const trendT2 = new TrendLine(chart, series, pointStart, pointEnd2, {
        lineColor: `rgba(${target2Color}, ${Math.min(cluster.prob * dataT2.prob * baseOpacityMult, 1)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT2);

      const trendT2L = new TrendLine(chart, series, pointStart2L, pointEnd2L, {
        lineColor: `rgba(${target2Color}, ${Math.min(cluster.prob * dataT2.prob * zoneOpacityMult, 0.5)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT2L);

      const trendT2H = new TrendLine(chart, series, pointStart2H, pointEnd2H, {
        lineColor: `rgba(${target2Color}, ${Math.min(cluster.prob * dataT2.prob * zoneOpacityMult, 0.5)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendT2H);

      // Cluster SL
      const pointStartSL = { time: lastWave.end, price: slPrice }
      const pointEndSL = { time: nextEndTime, price: slPrice }

      const trendSL = new TrendLine(chart, series, pointStartSL, pointEndSL, {
        lineColor: `rgba(255, 0, 0, ${Math.min(cluster.prob * stopOpacityMult, 1)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendSL);

      // Cluster TrailingSL
      const pointStartTSL = { time: lastWave.end, price: tslPrice }
      const pointEndTSL = { time: nextEndTime, price: tslPrice }

      const trendTSL = new TrendLine(chart, series, pointStartTSL, pointEndTSL, {
        lineColor: `rgba(255, 0, 144, ${Math.min(cluster.prob * stopOpacityMult, 1)})`,
        width: 2,
        showLabels: false,
      });
      series.attachPrimitive(trendTSL);
    }
  }

  // prev wave clusters
  if (BacktestSettings.showPrevExpectancy) {
    const isPrevMove = prePrevWave.type === 'move'
    // handleTrends(seriesExp, clusterExpectancy.prevPercent, prePrevWave, prevWave, lastWave.end)
    handleTrends(seriesExp, clusterExpectancy.prevRatio, prePrevWave, prevWave, lastWave.end, isPrevMove, BacktestSettings.adjustPrevExpectancy)
  }

  // current wave clusters
  // handleTrends(seriesExp, clusterExpectancy.currentPercent, prevWave, lastWave, nextEndTime)
  // handleTrends(seriesExp, clusterExpectancy.currentRatio, prevWave, lastWave, nextEndTime)
  if (BacktestSettings.showPullbackExpectancy) {
    handleTrends(seriesExp, clusterExpectancy.currentPullbackRatio, prevWave, lastWave, nextEndTime, false)
  }
  if (BacktestSettings.showMoveExpectancy) {
    handleTrends(seriesExp, clusterExpectancy.currentMoveRatio, prevWave, lastWave, nextEndTime, true)
  }

  // *******************************

  try {
    seriesExp.setData(seriesData)
  } catch (error) {
    console.log('seriesExp.setData drawClusterExpectancy error', error)
  }

  return series
}

// waves
// ***********************************************

export function drawLevelsLine(chart, levels, endTime, color = 'white', lineStyle = LineStyle.Solid) {
  const seriesData = [];
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    const point = { time: level.startTime, value: level.level }
    seriesData.push(point);
  }
  seriesData.sort((a, b) => a.time - b.time)

  // fix for charts same time limitation
  for (let i = 0; i < seriesData.length - 1; i++) {
    if (i > 0 && seriesData[i - 1].time === seriesData[i].time) {
      seriesData[i].time++
    }
  }
  // console.log('levelSeriesData', levelSeriesData)

  // Отрисовываем уровни на графике
  const levelSeries = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  try {
    levelSeries.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawLevelsLine', error)
  }

  return levelSeries
}

export function drawWaves(chart, waves, endTime, color = 'white', lineStyle = LineStyle.Solid, showIdx = false) {
  const series = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  const seriesData = []
  for (let i = 0; i < waves.length; i++) {
    const wave = waves[i];
    const prevWave = waves[i - 1]

    const xRatio = prevWave ? (wave.delta / prevWave.delta).toPrecision(2) : 0

    if (wave.direction === 'up') {
      seriesData.push({ time: wave.start, value: wave.low });
      // seriesData.push({ time: wave.end - 1, value: wave.high });
    } else {
      seriesData.push({ time: wave.start, value: wave.high });
      // seriesData.push({ time: wave.end - 1, value: wave.low });
    }

    if (!wave.type || wave.type === 'move') {
      const delta = wave.delta

      if (wave.direction === 'up') {
        const deltaPercent = (delta / wave.low) * 100
        const percent = deltaPercent < 100
          ? deltaPercent.toPrecision(2)
          : deltaPercent.toPrecision(3)
        const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
        const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
        const label = `${labelIdx}${percent}% x${xRatio}${labelCluster}`
        const point1 = { time: wave.start, price: wave.low };
        const point2 = { time: wave.end, price: wave.high, label };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: 'rgb(0, 255, 0)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0,0,0,0.3)",
          labelTextColor: "rgb(0, 255, 0)"
        });
        series.attachPrimitive(trend);
      } else {
        const deltaPercent = (delta / wave.high) * 100
        const percent = deltaPercent < 100
          ? deltaPercent.toPrecision(2)
          : deltaPercent.toPrecision(3)
        const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
        const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
        const label = `${labelIdx}${percent}% x${xRatio}${labelCluster}`
        const point1 = { time: wave.start, price: wave.high };
        const point2 = { time: wave.end, price: wave.low, label };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: 'rgb(255, 0, 0)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0,0,0,0.3)",
          labelTextColor: "rgb(255, 0, 0)"
        });
        series.attachPrimitive(trend);
      }
    } else {
      const delta = wave.high - wave.low
      const deltaPercent = (delta / wave.high) * 100
      const percent = deltaPercent < 100
        ? deltaPercent.toPrecision(2)
        : deltaPercent.toPrecision(3)

      if (wave.direction === 'up') {
        const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
        const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''

        const label = `${labelIdx}${percent}% x${xRatio}${labelCluster}`
        const point1 = { time: wave.start, price: wave.low };
        const point2 = { time: wave.end, price: wave.high, label };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: 'rgb(150, 220, 0)',
          width: 1,
          showLabels: true,
          labelBackgroundColor: "rgba(0,0,0,0.3)",
          labelTextColor: "rgb(150, 220, 0)"
        });
        series.attachPrimitive(trend);
      } else {
        const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
        const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
        const label = `${labelIdx}${percent}% x${xRatio}${labelCluster}`

        const point1 = { time: wave.start, price: wave.high };
        const point2 = { time: wave.end, price: wave.low, label };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: 'rgb(220, 150, 0)',
          width: 1,
          showLabels: true,
          labelBackgroundColor: "rgba(0,0,0,0.3)",
          labelTextColor: "rgb(220, 150, 0)"
        });
        series.attachPrimitive(trend);
      }
    }

  }

  const lastWave = waves[waves.length - 1]
  if (lastWave) {
    if (lastWave.direction === 'up') {
      seriesData.push({ time: lastWave.end, value: lastWave.high });
      // seriesData.push({ time: wave.end - 1, value: wave.high });
    } else {
      seriesData.push({ time: lastWave.end, value: lastWave.low });
      // seriesData.push({ time: wave.end - 1, value: wave.low });
    }
  }

  seriesData.sort((a, b) => a.time - b.time)
  // fix for charts same time limitation
  for (let i = 0; i < seriesData.length - 1; i++) {
    if (i > 0 && seriesData[i - 1].time === seriesData[i].time) {
      seriesData[i].time++
    }
  }
  // console.log('seriesData', seriesData)


  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawWaves', error)
  }

  return series
}

export function drawPredictedWaves(chart, waves, endTime, color = 'white', lineStyle = LineStyle.Solid, showIdx = false) {
  const series = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  const seriesData = []
  for (let i = 0; i < waves.length; i++) {
    const wave = waves[i];

    if (wave.direction === 'up') {
      seriesData.push({ time: wave.start, value: wave.low });
      // seriesData.push({ time: wave.end - 1, value: wave.high });
    } else {
      seriesData.push({ time: wave.start, value: wave.high });
      // seriesData.push({ time: wave.end - 1, value: wave.low });
    }

    if (wave.direction === 'up') {
      const label = `${wave.type}`
      const point1 = { time: wave.start, price: wave.low };
      const point2 = { time: wave.end, price: wave.high, label };
      const trend = new TrendLine(chart, series, point1, point2, {
        lineColor: 'rgb(150, 255, 150)',
        width: 3,
        showLabels: true,
        labelBackgroundColor: "rgba(255, 255, 255, 0)",
        labelTextColor: "rgb(150, 255, 150)"
      });
      series.attachPrimitive(trend);
    } else {
      const label = `${wave.type}`
      const point1 = { time: wave.start, price: wave.high };
      const point2 = { time: wave.end, price: wave.low, label };
      const trend = new TrendLine(chart, series, point1, point2, {
        lineColor: 'rgb(255, 150, 150)',
        width: 3,
        showLabels: true,
        labelBackgroundColor: "rgba(255, 255, 255, 0)",
        labelTextColor: "rgb(255, 150, 150)"
      });
      series.attachPrimitive(trend);
    }

  }

  const lastWave = waves[waves.length - 1]
  if (lastWave.direction === 'up') {
    seriesData.push({ time: lastWave.end, value: lastWave.high });
    // seriesData.push({ time: wave.end - 1, value: wave.high });
  } else {
    seriesData.push({ time: lastWave.end, value: lastWave.low });
    // seriesData.push({ time: wave.end - 1, value: wave.low });
  }

  seriesData.sort((a, b) => a.time - b.time)
  // fix for charts same time limitation
  for (let i = 0; i < seriesData.length - 1; i++) {
    if (i > 0 && seriesData[i - 1].time === seriesData[i].time) {
      // const diff = seriesData[i].time - seriesData[i - 1].time
      // seriesData[i].time += diff + 1
      seriesData[i].time++
    }
  }
  // console.log('seriesData', seriesData)


  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawWaves', error)
  }

  return series
}


export function drawPredictedWavesLine(chart, waves, endTime, color = 'white', lineStyle = LineStyle.Solid, showIdx = false) {
  const series = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  const seriesData = []
  for (let i = 0; i < waves.length; i++) {
    const wave = waves[i];

    if (wave.direction === 'up') {
      // seriesData.push({ time: wave.start, value: wave.high });
      seriesData.push({ time: wave.start, value: wave.low });
      seriesData.push({ time: wave.end - 1, value: wave.high });
    } else {
      // seriesData.push({ time: wave.start, value: wave.low });
      seriesData.push({ time: wave.start, value: wave.high });
      seriesData.push({ time: wave.end - 1, value: wave.low });
    }
  }

  const lastWave = waves[waves.length - 1]
  if (lastWave.direction === 'up') {
    seriesData.push({ time: lastWave.end, value: lastWave.low });
    // seriesData.push({ time: wave.end - 1, value: wave.high });
  } else {
    seriesData.push({ time: lastWave.end, value: lastWave.high });
    // seriesData.push({ time: wave.end - 1, value: wave.low });
  }

  seriesData.sort((a, b) => a.time - b.time)
  // fix for charts same time limitation
  for (let i = 0; i < seriesData.length - 1; i++) {
    if (i > 0 && seriesData[i - 1].time === seriesData[i].time) {
      seriesData[i].time++
    }
  }
  // console.log('seriesData', seriesData)


  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawWavesLine', error)
  }

  return series
}

export function drawWavesHierarchy(chart, waves, nesting, endTime, color = 'white', maxNestingToShowLabels = 0, showIdx = false, showCorrectionBox = false) {
  const allSeries = [];

  function drawWave(wave, nesting, lablePrefix = '') {
    if (wave.drawn) return
    wave.drawn = true

    let lineStyle = LineStyle.Solid
    let width = 3
    if (nesting === 1) {
      lineStyle = LineStyle.Dashed
      width = 2
    } else if (nesting > 1) {
      lineStyle = LineStyle.Dotted
      width = 1
    }


    const series = chart.addSeries(LineSeries, {
      color,
      lineWidth: 1,
      lineStyle,
      lastValueVisible: false,
      priceLineVisible: false,
    });
    const seriesData = [];
    const opacityInit = 1 - Math.log(1 + nesting) / Math.log(10)
    const opacity = (Math.max(0.4, opacityInit)).toFixed(2)

    let delta = wave.high - wave.low;
    let deltaPercent = 0
    if (wave.direction === 'up') {
      seriesData.push({ time: wave.start, value: wave.low })
      deltaPercent = (delta / wave.low) * 100
    } else {
      seriesData.push({ time: wave.start, value: wave.high })
      // delta = wave.low - wave.high
      deltaPercent = (delta / wave.high) * 100
    }


    const showLabels = nesting < maxNestingToShowLabels
    const prefixLetter = getLatinLetterByIndex(nesting)
    const prefix = maxNestingToShowLabels > 1 ? `${prefixLetter} ` : ''
    const percent = deltaPercent < 100
      ? deltaPercent.toPrecision(2)
      : deltaPercent.toPrecision(3)

    const labelIdx = showIdx ? `#${wave.idx} ` : ''
    const label = `${labelIdx}${lablePrefix} ${prefix}${percent}%`;
    let point1, point2, trendLineColor;

    if (wave.direction === 'up') {
      point1 = { time: wave.start, price: wave.low };
      point2 = { time: wave.end, price: wave.high, label };
      trendLineColor = `rgba(0, 255, 0, ${opacity})`;
    } else {
      point1 = { time: wave.start, price: wave.high };
      point2 = { time: wave.end, price: wave.low, label };
      trendLineColor = `rgba(255, 0, 0, ${opacity})`;
    }

    const trend = new TrendLine(chart, series, point1, point2, {
      lineColor: trendLineColor,
      width,
      showLabels,
      labelBackgroundColor: `rgba(0, 0, 0, ${opacity})`,
      labelTextColor: trendLineColor
    });
    series.attachPrimitive(trend);

    seriesData.sort((a, b) => a.time - b.time);
    series.setData(seriesData);
    allSeries.push(series);

    // Рекурсивно рисуем подволны коррекции
    if (wave.corrections && wave.corrections.length > 0) {
      wave.corrections.forEach(subWave => {
        drawWave(subWave, nesting + 1, 'Pullback');
      });

      if (showCorrectionBox && nesting <= maxNestingToShowLabels) {
        const boxSeries = drawCorrectionBox(chart, wave, nesting + 1)
        allSeries.push(...boxSeries);
      }
    }

    // Рекурсивно рисуем подволны каскадов
    const segmentsNesting = nesting + 1
    if (wave.segments && wave.segments.length > 0) {
      wave.segments.forEach(subWave => {
        drawWave(subWave, segmentsNesting + 1, 'Wave');
      });
    }
  }

  waves.forEach(wave => drawWave(wave, nesting));

  return allSeries;
}

function drawCorrectionBox(chart, wave, nesting, color = 'transparent') {
  let width = 3
  if (nesting > 2) {
    width = 2
  } else if (nesting > 3) {
    width = 1
  }

  const correction = wave.correction

  if (correction.start === correction.end) {
    // console.log('drawCorrectionBox 0 length', correction)
    return []
  }

  const seriesHigh = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  const seriesLow = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  const seriesHighData = [];
  const seriesLowData = [];

  const opacityInit = 1 - Math.log(1 + nesting) / Math.log(10)
  const opacity = (Math.max(0.5, opacityInit)).toFixed(2)
  const trendLineColor = `rgba(200, 200, 255, ${opacity})`;

  const hPoint1 = { time: correction.start, price: correction.high }
  const hPoint2 = { time: correction.end, price: correction.high }
  seriesHighData.push({ time: correction.start, value: correction.high })
  seriesHighData.push({ time: correction.end, value: correction.high })

  const lPoint1 = { time: correction.start, price: correction.low }
  const lPoint2 = { time: correction.end, price: correction.low }
  seriesLowData.push({ time: correction.start, value: correction.low })
  seriesLowData.push({ time: correction.end, value: correction.low })


  const trendHigh = new TrendLine(chart, seriesHigh, hPoint1, hPoint2, {
    lineColor: trendLineColor,
    width,
    showLabels: false,
  });
  seriesHigh.attachPrimitive(trendHigh);

  const trendLow = new TrendLine(chart, seriesHigh, lPoint1, lPoint2, {
    lineColor: trendLineColor,
    width,
    showLabels: false,
  });
  seriesHigh.attachPrimitive(trendLow);

  try {
    seriesHigh.setData(seriesHighData)
  } catch (error) {
    console.log('seriesHigh.setData drawCorrectionBox error', wave, error)
  }
  try {
    seriesLow.setData(seriesLowData)
  } catch (error) {
    console.log('seriesLow.setData drawCorrectionBox error', wave, error)
  }
  return [seriesHigh, seriesLow]
}

// wave fibo retracement
// ***********************************************

export function drawWavesFibo(currentChart, masterWave, nesting, intervalMinutes, maxWaveFiboNesting = 0, endTime) {
  const series = [];
  if (nesting >= maxWaveFiboNesting || !masterWave) {
    return series;
  }

  function drawWaveAndSubwaves(wave, nestLevel, showSegments, showCorrections, prefix = '') {
    if (nestLevel >= maxWaveFiboNesting) return
    // most important fibos drawn last in order to be on top

    if (showCorrections && wave.corrections && wave.corrections.length) {
      const lastCorrections = wave.corrections.slice(-2)
      const postfix = getLatinLetterByIndex(nestLevel)
      const correctionPrefix = prefix ? `${prefix}-Z:${postfix}` : `Z:${postfix}`;
      lastCorrections.forEach((subWave, i) => {
        drawWaveAndSubwaves(subWave, nestLevel + 1, false, true, correctionPrefix);
      });
    }

    if (showSegments && wave.segments && wave.segments.length) {
      const postfix = getLatinLetterByIndex(nestLevel)
      const segmentPrefix = prefix ? `${prefix}-S:${postfix}` : `S:${postfix}`;
      const lastSegment = wave.segments[wave.segments.length - 1];
      drawWaveAndSubwaves(lastSegment, nestLevel + 1, false, false, segmentPrefix);
    }

    const s = drawWaveFibo(currentChart, wave, nestLevel, intervalMinutes, maxWaveFiboNesting, prefix, endTime);
    series.push(s);
  }

  drawWaveAndSubwaves(masterWave, nesting, true, true);
  return series;
}

function drawWaveFibo(currentChart, masterWave, nesting, intervalMinutes, maxWaveFiboNesting, prefix = '', endTime) {
  // const rightShift = (maxWaveFiboNesting - nesting) * 60 * intervalMinutes * 5
  const rightShift = (nesting) * 60 * intervalMinutes * 20

  const wave = masterWave
  const { direction, high, delta, start, end, correction } = wave
  const corEnd = correction ? correction.end : end

  const ext = direction === 'up' ? fiboUpExtension : fiboDownExtension
  const fiboLevels = [...fiboCorrectionLevels, ...fiboHL, ...ext]
  const postfix = getLatinLetterByIndex(nesting)
  //const label = prefix ? `${prefix}:${postfix}` : postfix
  const label = prefix ? prefix : postfix

  const fixedEnd = corEnd + rightShift >= endTime ? endTime : corEnd + rightShift

  const levels = []
  for (let j = 0; j < fiboLevels.length; j++) {
    const mult = fiboLevels[j]
    const level = { level: high - delta * mult, fibo: mult, start, end: fixedEnd }
    levels.push(level)
    // console.log('cascadeStages fibo', prefix, level)
  }

  const series = drawFiboLines(currentChart, levels, high, start, fixedEnd, label)
  return series
}

function drawFiboLines(chart, levels, price, startTime, endTime, prefix) {
  const series = chart.addSeries(LineSeries, {
    color: 'transparent',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  const seriesData = [];
  seriesData.push({ time: startTime, value: price });
  seriesData.push({ time: endTime, value: price });

  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    // seriesData.push({ time: level.start, value: level.level });


    let alpha = 0.33
    let color = `rgba(125, 253, 258, ${alpha})`
    if (level.fibo === 0.5) {
      alpha = 1
      color = `rgba(255, 100, 0, ${alpha})`
    } else if (level.fibo === 0.618 || level.fibo === 0.382) {
      alpha = 0.75
      color = `rgba(238, 238, 76, ${alpha})`
    } else if (level.fibo === 0.786 || level.fibo === 0.236) {
      alpha = 0.66
      color = `rgba(230, 50, 250, ${alpha})`
    }


    const label = `${prefix} ${level.fibo}` // ${level.level.toPrecision(4)}
    const point1 = { time: level.start, price: level.level };
    const point2 = { time: level.end, price: level.level, label };

    const trend = new TrendLine(chart, series, point1, point2, {
      lineColor: color,
      width: 1,
      showLabels: true,
      labelBackgroundColor: `rgba(0, 0, 0, ${alpha})`,
      labelTextColor: color,
    });
    series.attachPrimitive(trend);
  }

  // for (let i = 0; i < levels.length; i++) {
  //   const level = levels[i];
  //   seriesData.push({ time: level.end, value: level.level });
  // }
  // seriesData.push({ time: endTime + levels.length * 3, value: 0 });

  seriesData.sort((a, b) => a.time - b.time)
  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawFiboLines', seriesData, error)
  }

  return series

}



export function getWavesTrendLines(waves, endTime) {
  if (waves.length < 3) return { low: null, high: null };

  const w1 = waves[waves.length - 3];
  const w2 = waves[waves.length - 1];

  // Различие в расчёте времени для highs и lows в зависимости от направления первой волны
  const totalTimeHighs = w1.direction === 'up' ? (w2.end - w1.end) : (w2.start - w1.start);
  const totalTimeLows = w1.direction === 'up' ? (w2.start - w1.start) : (w2.end - w1.end);

  // Вычисление наклона и конечных точек
  const slopeHigh = (w2.high - w1.high) / totalTimeHighs;
  const slopeLow = (w2.low - w1.low) / totalTimeLows;

  const highsEndPrice = w1.direction === 'up'
    ? w2.high + slopeHigh * (endTime - w2.end)
    : w2.high + slopeHigh * (endTime - w2.start);

  const lowsEndPrice = w1.direction === 'up'
    ? w2.low + slopeLow * (endTime - w2.start)
    : w2.low + slopeLow * (endTime - w2.end);

  return {
    high: {
      start: {
        time: w1.direction === 'up' ? w1.end : w1.start,
        price: w1.high
      },
      end: {
        time: endTime,
        price: highsEndPrice
      }
    },
    low: {
      start: {
        time: w1.direction === 'up' ? w1.start : w1.end,
        price: w1.low
      },
      end: {
        time: endTime,
        price: lowsEndPrice
      }
    }
  };
}

export function drawWavesTrendLines(chart, trendLinesData) {
  const seriesOptions = {
    color: 'transparent',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastTimeVisible: false,
    priceLineVisible: false,
  };

  const series = {
    high: chart.addSeries(LineSeries, seriesOptions),
    low: chart.addSeries(LineSeries, seriesOptions),
  };

  const colors = {
    high: 'rgba(50, 100, 255, 1)',
    low: 'rgba(50, 100, 255, 1)',
  };

  function createTrendLine(data, color) {
    const startPoint = { time: data.start.time, price: data.start.price };
    const endPoint = { time: data.end.time, price: data.end.price };

    const trendLine = new TrendLine(chart, series[color], startPoint, endPoint, {
      lineColor: colors[color],
      width: 2,
      showLabels: true,
      labelBackgroundColor: "rgba(255, 255, 255, 0)",
      labelTextColor: colors[color],
    });

    series[color].attachPrimitive(trendLine);
  }

  if (trendLinesData.high) {
    try {
      const data = trendLinesData.high;
      series.high.setData([{ time: data.start.time, value: data.start.price }, { time: data.end.time, value: data.end.price }]);
      createTrendLine(data, 'high');
    } catch (error) {
      console.log('Error setting data for trend line (high)', error);
    }
  }

  if (trendLinesData.low) {
    try {
      const data = trendLinesData.low;
      series.low.setData([{ time: data.start.time, value: data.start.price }, { time: data.end.time, value: data.end.price }]);
      createTrendLine(data, 'low');
    } catch (error) {
      console.log('Error setting data for trend line (low)', error);
    }
  }

  return [series.high, series.low];
}


// levels
// ***********************************************

export function drawKeyLevels(chart, keyLevels, endTime) {
  const { highLevels, lowLevels } = keyLevels

  const highSeriesData = [];
  for (let i = 0; i < highLevels.length; i++) {
    const level = highLevels[i];
    highSeriesData.push({ time: level.startTime, value: level.level });
    if (i < (highLevels.length - 1)) {
      highSeriesData.push({
        time: highLevels[i + 1].startTime - 1,
        value: level.level
      });

    } else {
      highSeriesData.push({
        time: endTime + 1,
        value: level.level
      });
    }
  }

  const lowSeriesData = [];
  for (let i = 0; i < lowLevels.length; i++) {
    const level = lowLevels[i];
    lowSeriesData.push({ time: level.startTime, value: level.level });
    if (i < (lowLevels.length - 1)) {
      lowSeriesData.push({
        time: lowLevels[i + 1].startTime - 1,
        value: level.level
      });
    } else {
      lowSeriesData.push({
        time: endTime + 1,
        value: level.level
      });
    }
  }
  // console.log('highSeriesData', highSeriesData)
  // console.log('lowSeriesData', lowSeriesData)

  // Отрисовываем высокие уровни на графике
  const highSeries = chart.addSeries(LineSeries, {
    color: 'red',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  highSeriesData.sort((a, b) => a.time - b.time)
  try {
    highSeries.setData(highSeriesData);
  } catch (error) {
    console.log('series.setData drawKeyLevels', error)
  }


  // Отрисовываем низкие уровни на графике
  const lowSeries = chart.addSeries(LineSeries, {
    color: 'blue',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  lowSeriesData.sort((a, b) => a.time - b.time)
  try {
    lowSeries.setData(lowSeriesData);
  } catch (error) {
    console.log('series.setData drawKeyLevels', error)
  }

}

export function drawImportantLevels(chart, importantLevels, endTime, latestClose, alphaMult = 1, LEVEL_HIDE_DISTANCE) {
  if (!chart) return []
  if (!importantLevels.length) return []
  const data = []
  const allSeries = []

  try {
    const levelSeries = chart.addSeries(LineSeries, {
      color: '#FFFFFF00',
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
    });
    allSeries.push(levelSeries)

    importantLevels.forEach((level) => {
      if (level.level * (1 + LEVEL_HIDE_DISTANCE) < latestClose
        || level.level * (1 - LEVEL_HIDE_DISTANCE) > latestClose) {
        return null
      }

      const alpha0 = level.unbeat ? 1 : 0.5
      const mainColor = getImportantLevelColor(level, alpha0)

      const priceLine = {
        price: level.level,
        color: mainColor,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: !!level.unbeat,
        title: `${level.type} ${level.touches || 1} ${level.mergedLevels ? level.mergedLevels.length : 1}`,
      };
      data.push({ time: level.startTime, value: latestClose });

      levelSeries.createPriceLine(priceLine);

      // ***********************************************
      // Lower and upper bound Base Line
      // ***********************************************
      const series = chart.addSeries(LineSeries, {
        color: '#FFFFFF00',
        lineWidth: 1,
        lastValueVisible: false,
        priceLineVisible: false,
      });
      allSeries.push(series)

      const alpha1 = level.unbeat ? 0.33 : 0.1
      const alpha2 = level.unbeat ? 0.66 : 0.33
      const lightColor = getImportantLevelColor(level, alpha1 * alphaMult)
      const darkColor = getImportantLevelColor(level, alpha2 * alphaMult)

      // Main level
      const mainPriceLine = {
        price: level.level,
        color: mainColor,
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        // axisLabelVisible: true,
        // title: 'Level ' + level.level.toFixed(2),
      };
      series.createPriceLine(mainPriceLine);

      // ***********************************************
      // Lower and upper bound lines
      // ***********************************************

      const opts = {
        baseValue: {
          type: 'price', price: level.level
        },
        lastValueVisible: false,
        priceLineVisible: false,
        lineStyle: LineStyle.Dotted,
        lineWidth: 1,
        // top colors
        topLineColor: darkColor,
        topFillColor1: darkColor,
        topFillColor2: lightColor,
        // bottom colors
        bottomLineColor: darkColor,
        bottomFillColor1: lightColor,
        bottomFillColor2: darkColor,
      }

      const boundSeriesUP = chart.addSeries(BaselineSeries, opts);
      const upperBound = level.upperBound //(level.level + level.atr) || level.upperBound
      const lowerBound = level.lowerBound //(level.level - level.atr) || level.lowerBound


      try {
        boundSeriesUP.setData([
          { time: level.startTime, value: upperBound },
          { time: endTime > level.startTime ? endTime : endTime + 61, value: upperBound }
        ]);
      } catch (error) {
        console.log('series.setData boundSeriesUP', error)
      }
      allSeries.push(boundSeriesUP)

      const boundSeriesDown = chart.addSeries(BaselineSeries, opts);

      try {
        boundSeriesDown.setData([
          { time: level.startTime, value: lowerBound },
          { time: endTime > level.startTime ? endTime : endTime + 61, value: lowerBound }
        ]);
      } catch (error) {
        console.log('series.setData boundSeriesDown', error)
      }
      allSeries.push(boundSeriesDown)
    });

    data.push({ time: endTime + 61, value: importantLevels[0].level }); // bugfix point
    data.sort((a, b) => a.time - b.time)

    try {
      levelSeries.setData(data);
    } catch (error) {
      console.log('series.setData drawImportantLevels', error)
    }

  } catch (e) {
    console.error('drawImportantLevels', e)
  }
  return allSeries
}

export function getImportantLevelColor(level, alpha = 1) {
  if (level.type === 'resistance') {
    return `rgba(255,0,0,${alpha})`; // Red for high levels
  } else if (level.type === 'support') {
    return `rgba(0,255,0,${alpha})`; // Green for low levels
  } else if (level.type === 'fibo') {
    return `rgba(255,255,255,${alpha})`; // white for fibo levels
  } else {
    return `rgba(255,0,255,${alpha})`; // Blue for double levels
  }
}

// fibo

export function drawFiboLevels(chart, fiboLevels, endTime) {
  if (!chart) return []
  const data = []
  const allSeries = []

  try {
    const levelSeries = chart.addSeries(LineSeries, {
      color: '#FFFFFF00',
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
    });
    allSeries.push(levelSeries)

    fiboLevels.forEach((level, i) => {
      const alpha0 = 0.66 //level.mergedLevels ? Math.min(1, 0.5 * level.mergedLevels.length) : 0.3
      const mainColor = getImportantLevelColor(level, alpha0)

      const priceLine = {
        price: level.level,
        color: mainColor,
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false, //!!level.unbeat,
        title: `${level.type} ${level.mult}`, //${level.mergedLevels ? level.mergedLevels.length : 1}
      };
      data.push({ time: endTime + i, value: level.level });

      levelSeries.createPriceLine(priceLine);
    });

    // data.push({ time: endTime + 61, value: fiboLevels[0].level }); // bugfix point
    data.sort((a, b) => a.time - b.time)

    try {
      levelSeries.setData(data);
    } catch (error) {
      console.log('series.setData drawFiboLevels', error)
    }


  } catch (e) {
    console.error('drawFiboLevels', e)
  }
  return allSeries
}



export function drawBacktestWaves(chart, waveBacktest, endTime, color = 'white', lineStyle = LineStyle.Solid, showIdx = false) {
  if (!waveBacktest) return null
  const { wavesChanges: waves } = waveBacktest

  const series = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  // remove time dubs
  const seriesData = []
  const timesArr = []
  for (let i = 0; i < waves?.length; i++) {
    const wave = waves[i];
    if (!timesArr.includes(wave.start)) {
      seriesData.push({ time: wave.start, value: wave.low });
      timesArr.push(wave.start)
    }
    if (!timesArr.includes(wave.end)) {
      seriesData.push({ time: wave.end, value: wave.low });
      timesArr.push(wave.end)
    }
  }
  seriesData.sort((a, b) => a.time - b.time)


  const showUP = true
  const showDOWN = true
  const showHighs = true
  const showLows = true
  const showLevels = true
  const maxShowLevels = 1
  const ignoreDirection = true
  const showSL = false
  const showEnterStart = false
  const showTPPercent = false
  const showTPRatio = false
  const showTPRatioClusters = false
  const showPredictedEnd = false
  const show4Points0 = false
  const show4Points1 = false

  for (let i = 0; i < waves?.length; i++) {
    const wave = waves[i];

    let opacity = 0.25
    let labelOpacity = opacity
    let prefix = ''
    const dir = wave.direction === 'up'
      ? 'U'
      : wave.direction === 'down'
        ? 'D'
        : 'I'
    if (wave.newWave) {
      opacity = 0.7
      labelOpacity = 1
      prefix = `N-${dir} `
    } else if (wave.lastUp) {
      opacity = 0.3
      labelOpacity = opacity
      prefix = `U-${dir} `
    } else if (wave.extremUp) {
      opacity = 0.1
      labelOpacity = opacity
      prefix = `E-${dir} `
    }

    const showWaves = true

    if (showWaves) {
      if (!wave.type || wave.type === 'move') {
        const delta = wave.high - wave.low
        if (wave.direction === 'up') {
          const deltaPercent = (delta / wave.low) * 100
          const percent = deltaPercent < 100
            ? deltaPercent.toPrecision(2)
            : deltaPercent.toPrecision(3)
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.low };
          const point2 = { time: wave.end, price: wave.high, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(0, 255, 0, ${opacity})`,
            width: 2,
            showLabels: true,
            labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
            labelTextColor: `rgb(0, 255, 0, ${labelOpacity})`
          });
          series.attachPrimitive(trend);
        } else {
          const deltaPercent = (delta / wave.high) * 100
          const percent = deltaPercent < 100
            ? deltaPercent.toPrecision(2)
            : deltaPercent.toPrecision(3)
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.high };
          const point2 = { time: wave.end, price: wave.low, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(255, 255, 0, ${opacity})`,
            width: 2,
            showLabels: true,
            labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
            labelTextColor: `rgb(255, 255, 0, ${labelOpacity})`
          });
          series.attachPrimitive(trend);
        }
      } else {
        const delta = wave.high - wave.low
        const deltaPercent = (delta / wave.high) * 100
        const percent = deltaPercent < 100
          ? deltaPercent.toPrecision(2)
          : deltaPercent.toPrecision(3)

        if (wave.direction === 'up') {
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''

          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.low };
          const point2 = { time: wave.end, price: wave.high, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(150, 220, 0, ${opacity})`,
            width: 1,
            showLabels: true,
            labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
            labelTextColor: `rgb(150, 220, 0, ${labelOpacity})`,
          });
          series.attachPrimitive(trend);
        } else {
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`

          const point1 = { time: wave.start, price: wave.high };
          const point2 = { time: wave.end, price: wave.low, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(220, 150, 0, ${opacity})`,
            width: 1,
            showLabels: true,
            labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
            labelTextColor: `rgb(220, 150, 0, ${labelOpacity})`,
          });
          series.attachPrimitive(trend);
        }
      }
    }



    if (i > 0 && i < waves.length - 1) {
      const windowTimeInCandles = 2
      const timeMult = 60

      const prevWave = waves[i - 1]
      const nextWave = waves[i + 1]
      const usedWave = wave

      const lastRealWave = usedWave?.lastRealWave
      const lastWaveUpdated = usedWave?.waveContinuation

      const wCalc = wave?.calc

      // const t1 = level.end
      // const t2 = nextWave.end

      const t1 = prevWave.end || wave.start
      const t2 = wave.end

      // wave.newWave && 
      if (ignoreDirection || wave.direction === 'up') {
        if (showHighs && wCalc?.closestHighs?.length) {
          wCalc.closestHighs.slice(-maxShowLevels).forEach((level, i) => {
            if (i > 0) return // only first level

            const isTimeActive = level.start + windowTimeInCandles * timeMult < wave.end
            if (!isTimeActive) return

            if (showLevels) {
              const point1 = { time: t1, price: level.high }
              const point2 = { time: t2, price: level.high, label: 'UP' };
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 255, 0.99)',
                width: 1,
                showLabels: false,
                labelBackgroundColor: "rgba(255, 255, 255, 0)",
                labelTextColor: 'rgba(255, 255, 255, 0.99)',
              });
              series.attachPrimitive(trend);
            }


            const pullbackTargetDeltaPrevWave = wCalc?.pullbackTargetDeltaPrevWave
            if (showPredictedEnd && lastRealWave && pullbackTargetDeltaPrevWave) {
              const price = pullbackTargetDeltaPrevWave.price

              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(0, 200, 255, 0.99)',
                width: 2,
                showLabels: false,
                labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
                labelTextColor: 'rgba(0, 200, 255, 0.99)',
              });
              series.attachPrimitive(trend);
            }

            const moveTargetDeltaPrevWave = wCalc?.moveTargetDeltaPrevWave
            if (showPredictedEnd && lastRealWave && moveTargetDeltaPrevWave) {
              const price = moveTargetDeltaPrevWave.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 0, 255, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }


            const pullbackTargetDeltaLastWaveUpdated = wCalc?.pullbackTargetDeltaLastWaveUpdated
            if (showPredictedEnd && lastWaveUpdated && pullbackTargetDeltaLastWaveUpdated) {
              const price = pullbackTargetDeltaLastWaveUpdated.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 132, 0, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const moveTargetDeltaLastWaveUpdated = wCalc?.moveTargetDeltaLastWaveUpdated
            if (showPredictedEnd && lastWaveUpdated && moveTargetDeltaLastWaveUpdated) {
              const price = moveTargetDeltaLastWaveUpdated.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 0, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const bounceSLPercentLong = wCalc?.bounceSLPercentLong?.optimal?.limit
            if (showSL && bounceSLPercentLong) {
              const sl = level.high - level.high * bounceSLPercentLong
              const point1 = { time: t1, price: sl }
              const point2 = { time: t2, price: sl }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 0, 0, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const bounceEnterLong = wCalc?.bounceEnterLong?.optimal?.limit
            if (showEnterStart && bounceEnterLong) {
              const enter = level.high - level.high * bounceEnterLong
              const point1 = { time: t1, price: enter }
              const point2 = { time: t2, price: enter }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 0, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const waveTargetPercent = wCalc?.waveTargetPercent
            if (showTPPercent && waveTargetPercent) {
              const price = level.high - level.high * waveTargetPercent
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(155, 255, 155, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const waveTargetDelta = wave.waveTargetDeltaUp
            if (showTPRatio && waveTargetDelta) {
              const price = level.high - waveTargetDelta
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(15, 255, 205, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const clusterTargetsUp = wave.clusterTargetsUp
            if (showTPRatioClusters && clusterTargetsUp.length) {
              for (const target of clusterTargetsUp) {
                const price = level.high - target
                const point1 = { time: t1, price }
                const point2 = { time: t2, price }
                const trend = new TrendLine(chart, series, point1, point2, {
                  lineColor: 'rgba(15, 205, 240, 0.8)',
                  width: 1,
                  showLabels: false,
                });
                series.attachPrimitive(trend);
              }
            }

          });
        }
      }

      if (ignoreDirection || wave.direction === 'down') {
        if (showLows && wCalc?.closestLows?.length) {
          wCalc.closestLows.slice(-maxShowLevels).forEach((level, i) => {
            if (i > 0) return // only first level
            const isTimeActive = level.start + windowTimeInCandles * timeMult < wave.end
            if (!isTimeActive) return

            if (showLevels) {
              const point1 = { time: t1, price: level.low }
              const point2 = { time: t2, price: level.low, label: 'DN' }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 255, 0.99)',
                width: 1,
                showLabels: false,
                labelBackgroundColor: "rgba(255, 255, 255, 0)",
                labelTextColor: 'rgba(255, 255, 255, 0.99)',
              });
              series.attachPrimitive(trend);
            }

            const pullbackTargetDeltaPrevWave = wCalc?.pullbackTargetDeltaPrevWave
            if (showPredictedEnd && pullbackTargetDeltaPrevWave) {
              const price = pullbackTargetDeltaPrevWave.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(0, 200, 255, 0.99)',
                width: 2,
                showLabels: false,
                labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
                labelTextColor: 'rgba(0, 200, 255, 0.99)',
              });
              series.attachPrimitive(trend);
            }

            const moveTargetDeltaPrevWave = wCalc?.moveTargetDeltaPrevWave
            if (showPredictedEnd && moveTargetDeltaPrevWave) {
              const price = moveTargetDeltaPrevWave.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 0, 255, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const pullbackTargetDeltaLastWaveUpdated = wCalc?.pullbackTargetDeltaLastWaveUpdated
            if (showPredictedEnd && pullbackTargetDeltaLastWaveUpdated) {
              const price = pullbackTargetDeltaLastWaveUpdated.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 132, 0, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const moveTargetDeltaLastWaveUpdated = wCalc?.moveTargetDeltaLastWaveUpdated
            if (showPredictedEnd && moveTargetDeltaLastWaveUpdated) {
              const price = moveTargetDeltaLastWaveUpdated.price
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 0, 0.99)',
                width: 2,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const bounceSLPercentShort = wCalc?.bounceSLPercentShort?.optimal?.limit
            if (showSL && bounceSLPercentShort) {
              const sl = level.low + level.low * bounceSLPercentShort
              const point1 = { time: t1, price: sl }
              const point2 = { time: t2, price: sl }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 0, 0, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const bounceEnterShort = wCalc?.bounceEnterShort?.optimal?.limit
            if (showEnterStart && bounceEnterShort) {
              const enter = level.low + level.low * bounceEnterShort
              const point1 = { time: t1, price: enter }
              const point2 = { time: t2, price: enter }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 0, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const waveTargetPercent = wCalc?.waveTargetPercent
            if (showTPPercent && waveTargetPercent) {
              const price = level.low + level.low * waveTargetPercent
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(155, 255, 155, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const waveTargetDelta = wave.waveTargetDeltaDown
            if (showTPRatio && waveTargetDelta) {
              const price = level.low + waveTargetDelta
              const point1 = { time: t1, price }
              const point2 = { time: t2, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(15, 255, 205, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const clusterTargetsDown = wave.clusterTargetsDown
            if (showTPRatioClusters && clusterTargetsDown.length) {
              for (const target of clusterTargetsDown) {
                const price = level.low + target
                const point1 = { time: t1, price }
                const point2 = { time: t2, price }
                const trend = new TrendLine(chart, series, point1, point2, {
                  lineColor: 'rgba(15, 205, 240, 0.8)',
                  width: 1,
                  showLabels: false,
                });
                series.attachPrimitive(trend);
              }
            }
          });
        }
      }

      // const wavesFourPoints0 = wCalc?.wavesFourPoints0
      // const wavesFourPoints1 = wCalc?.wavesFourPoints1
      // let fp0 = 'NA'
      // if (wavesFourPoints0) {
      //   if (wavesFourPoints0.combined === 'up') fp0 = 'U'
      //   if (wavesFourPoints0.combined === 'down') fp0 = 'D'
      //   if (wavesFourPoints0.combined === 'convergence') fp0 = 'CON'
      //   if (wavesFourPoints0.combined === 'divergence') fp0 = 'DIV'
      // }
      // let fp1 = 'NA'
      // if (wavesFourPoints1) {
      //   if (wavesFourPoints1.combined === 'up') fp1 = 'U'
      //   if (wavesFourPoints1.combined === 'down') fp1 = 'D'
      //   if (wavesFourPoints1.combined === 'convergence') fp1 = 'CON'
      //   if (wavesFourPoints1.combined === 'divergence') fp1 = 'DIV'
      // }
      // const label = `${fp0}_${fp1}`

      if (show4Points0 && wCalc?.wavesFourPoints0) {
        let color
        if (usedWave.wavesFourPoints0.combined === 'up') color = 'rgba(0, 255, 0, 0.99)'
        if (usedWave.wavesFourPoints0.combined === 'down') color = 'rgba(255, 0, 0, 0.99)'
        if (usedWave.wavesFourPoints0.combined === 'convergence') color = 'rgba(255, 255, 0, 0.99)'
        if (usedWave.wavesFourPoints0.combined === 'divergence') color = 'rgba(255, 0, 255, 0.99)'

        const point1 = {
          time: prevWave.end || wave.start,
          price: prevWave.high
        };
        const point2 = {
          time: wave.end,
          price: wave.high,
          label: `4P0_${usedWave.wavesFourPoints0.combined}`
        };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: color,
          width: 4,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
          labelTextColor: `rgba(255, 255, 255, ${labelOpacity})`
        });
        series.attachPrimitive(trend);
      }

      if (show4Points1 && wCalc?.wavesFourPoints1) {
        let color
        if (usedWave.wavesFourPoints1.combined === 'up') color = 'rgba(0, 255, 0, 0.99)'
        if (usedWave.wavesFourPoints1.combined === 'down') color = 'rgba(255, 0, 0, 0.99)'
        if (usedWave.wavesFourPoints1.combined === 'convergence') color = 'rgba(255, 255, 0, 0.99)'
        if (usedWave.wavesFourPoints1.combined === 'divergence') color = 'rgba(255, 0, 255, 0.99)'

        const point1 = {
          time: prevWave.end || wave.start,
          price: prevWave.low
        };
        const point2 = {
          time: wave.end,
          price: wave.low,
          label: `4P1_${usedWave.wavesFourPoints1.combined}`
        };
        const trend = new TrendLine(chart, series, point1, point2, {
          lineColor: color,
          width: 4,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.3)",
          labelTextColor: `rgba(255, 255, 255, ${labelOpacity})`
        });
        series.attachPrimitive(trend);
      }

    }


  }


  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawWaves', error)
  }

  return series
}

export function drawBacktestInverseWaves(chart, waves, endTime, color = 'white', lineStyle = LineStyle.Solid, showIdx = false) {
  const series = chart.addSeries(LineSeries, {
    color,
    lineWidth: 1,
    lineStyle,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  // remove time dubs
  const seriesData = []
  const timesArr = []
  for (let i = 0; i < waves?.length; i++) {
    const wave = waves[i];
    if (!timesArr.includes(wave.start)) {
      seriesData.push({ time: wave.start, value: wave.low });
      timesArr.push(wave.start)
    }
    if (!timesArr.includes(wave.end)) {
      seriesData.push({ time: wave.end, value: wave.low });
      timesArr.push(wave.end)
    }
  }
  seriesData.sort((a, b) => a.time - b.time)


  for (let i = 0; i < waves?.length; i++) {
    const wave = waves[i];

    let opacity = 0.25
    let prefix = ''
    if (wave.newWave) {
      opacity = 0.5
      prefix = 'N '
    } else if (wave.lastUp) {
      opacity = 0.3
      prefix = 'U '
    } else if (wave.extremUp) {
      opacity = 0.1
      prefix = 'E '
    }

    const showWaves = true

    if (showWaves) {
      if (!wave.type || wave.type === 'move') {
        const delta = wave.high - wave.low
        if (wave.direction === 'up') {
          const deltaPercent = (delta / wave.low) * 100
          const percent = deltaPercent < 100
            ? deltaPercent.toPrecision(2)
            : deltaPercent.toPrecision(3)
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.low };
          const point2 = { time: wave.end, price: wave.high, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(0, 255, 0, ${opacity})`,
            width: 2,
            showLabels: true,
            labelBackgroundColor: "rgba(255, 255, 255, 0)",
            labelTextColor: `rgb(0, 255, 0, ${opacity})`
          });
          series.attachPrimitive(trend);
        } else {
          const deltaPercent = (delta / wave.high) * 100
          const percent = deltaPercent < 100
            ? deltaPercent.toPrecision(2)
            : deltaPercent.toPrecision(3)
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.high };
          const point2 = { time: wave.end, price: wave.low, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(255, 255, 0, ${opacity})`,
            width: 2,
            showLabels: true,
            labelBackgroundColor: "rgba(255, 255, 255, 0)",
            labelTextColor: `rgb(255, 255, 0, ${opacity})`
          });
          series.attachPrimitive(trend);
        }
      } else {
        const delta = wave.high - wave.low
        const deltaPercent = (delta / wave.high) * 100
        const percent = deltaPercent < 100
          ? deltaPercent.toPrecision(2)
          : deltaPercent.toPrecision(3)

        if (wave.direction === 'up') {
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''

          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`
          const point1 = { time: wave.start, price: wave.low };
          const point2 = { time: wave.end, price: wave.high, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(150, 220, 0, ${opacity})`,
            width: 1,
            showLabels: true,
            labelBackgroundColor: "rgba(255, 255, 255, 0)",
            labelTextColor: `rgb(150, 220, 0, ${opacity})`,
          });
          series.attachPrimitive(trend);
        } else {
          const labelIdx = showIdx ? `#${wave.idx || i} ` : ''
          const labelCluster = wave.cluster !== undefined ? ` (${wave.cluster})` : ''
          const label = `${prefix}${labelIdx}${percent}%${labelCluster}`

          const point1 = { time: wave.start, price: wave.high };
          const point2 = { time: wave.end, price: wave.low, label };
          const trend = new TrendLine(chart, series, point1, point2, {
            lineColor: `rgb(220, 150, 0, ${opacity})`,
            width: 1,
            showLabels: true,
            labelBackgroundColor: "rgba(255, 255, 255, 0)",
            labelTextColor: `rgb(220, 150, 0, ${opacity})`,
          });
          series.attachPrimitive(trend);
        }
      }
    }

    const showHighs = true
    const showLows = true
    const showLevels = true
    const showSL = true
    const showEnterStart = true
    const showTPPercent = true
    const showTPRatio = true
    const showTPRatioClusters = true

    if (i > 0 && i < waves.length - 1) {
      const windowTimeInCandles = 2
      const timeMult = 60

      const prevWave = waves[i - 1]
      const nextWave = waves[i + 1]
      const usedWave = wave

      // wave.newWave && 
      if (wave.direction === 'up') {
        if (showHighs && usedWave?.closestHighs?.length) {
          usedWave.closestHighs.forEach((level, i) => {
            const isTimeActive = level.start + windowTimeInCandles * timeMult < wave.end
            // if (!isTimeActive) return

            if (showLevels) {
              const point1 = { time: level.end, price: level.high }
              const point2 = { time: nextWave.end, price: level.high }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 255, 255, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const waveTargetDelta = wave.waveTargetDeltaUp
            if (showTPRatio && waveTargetDelta) {
              const price = level.high - waveTargetDelta
              const point1 = { time: level.end, price }
              const point2 = { time: nextWave.end, price }
              const trend = new TrendLine(chart, series, point1, point2, {
                lineColor: 'rgba(255, 88, 205, 0.99)',
                width: 1,
                showLabels: false,
              });
              series.attachPrimitive(trend);
            }

            const virtualLevel = waveTargetDelta ? level.high - waveTargetDelta : null
            if (virtualLevel) {

              const bounceSLPercentLong = usedWave?.bounceSLPercentLong?.optimal?.limit
              if (showSL && bounceSLPercentLong) {
                const sl = virtualLevel - virtualLevel * bounceSLPercentLong * 1.0
                const point1 = { time: level.end, price: sl }
                const point2 = { time: nextWave.end, price: sl }
                const trend = new TrendLine(chart, series, point1, point2, {
                  lineColor: 'rgba(255, 0, 0, 0.99)',
                  width: 1,
                  showLabels: false,
                });
                series.attachPrimitive(trend);
              }

              const bounceEnterLong = usedWave?.bounceEnterLong?.optimal?.limit
              if (showEnterStart && bounceEnterLong) {
                const slShift = bounceSLPercentLong ? virtualLevel * bounceSLPercentLong : Infinity
                const shift = Math.min(virtualLevel * bounceEnterLong, slShift)
                const enter = virtualLevel + shift
                const point1 = { time: level.end, price: enter }
                const point2 = { time: nextWave.end, price: enter }
                const trend = new TrendLine(chart, series, point1, point2, {
                  lineColor: 'rgba(255, 255, 0, 0.99)',
                  width: 1,
                  showLabels: false,
                });
                series.attachPrimitive(trend);
              }

              const waveTargetPercent = usedWave?.waveTargetPercent
              if (showTPPercent && waveTargetPercent) {
                const price = virtualLevel + virtualLevel * waveTargetPercent
                const point1 = { time: level.end, price }
                const point2 = { time: nextWave.end, price }
                const trend = new TrendLine(chart, series, point1, point2, {
                  lineColor: 'rgba(155, 255, 155, 0.99)',
                  width: 1,
                  showLabels: false,
                });
                series.attachPrimitive(trend);
              }
            }

            // const bounceSLPercentLong = usedWave?.bounceSLPercentLong?.optimal?.limit
            // if (showSL && bounceSLPercentLong) {
            //   const sl = level.high + level.high * bounceSLPercentLong
            //   const point1 = { time: level.end, price: sl }
            //   const point2 = { time: nextWave.end, price: sl }
            //   const trend = new TrendLine(chart, series, point1, point2, {
            //     lineColor: 'rgba(255, 0, 0, 0.99)',
            //     width: 1,
            //     showLabels: false,
            //   });
            //   series.attachPrimitive(trend);
            // }

            // const bounceEnterLong = usedWave?.bounceEnterLong?.optimal?.limit
            // if (showEnterStart && bounceEnterLong) {
            //   const enter = level.high - level.high * bounceEnterLong
            //   const point1 = { time: level.end, price: enter }
            //   const point2 = { time: nextWave.end, price: enter }
            //   const trend = new TrendLine(chart, series, point1, point2, {
            //     lineColor: 'rgba(255, 255, 0, 0.99)',
            //     width: 1,
            //     showLabels: false,
            //   });
            //   series.attachPrimitive(trend);
            // }

            // const waveTargetPercent = usedWave?.waveTargetPercent
            // if (showTPPercent && waveTargetPercent) {
            //   const price = level.high - level.high * waveTargetPercent
            //   const point1 = { time: level.end, price }
            //   const point2 = { time: nextWave.end, price }
            //   const trend = new TrendLine(chart, series, point1, point2, {
            //     lineColor: 'rgba(155, 255, 155, 0.99)',
            //     width: 1,
            //     showLabels: false,
            //   });
            //   series.attachPrimitive(trend);
            // }

            // const clusterTargetsUp = wave.clusterTargetsUp
            // if (showTPRatioClusters && clusterTargetsUp.length) {
            //   for (const target of clusterTargetsUp) {
            //     const price = level.high - target
            //     const point1 = { time: level.end, price }
            //     const point2 = { time: nextWave.end, price }
            //     const trend = new TrendLine(chart, series, point1, point2, {
            //       lineColor: 'rgba(15, 205, 240, 0.8)',
            //       width: 1,
            //       showLabels: false,
            //     });
            //     series.attachPrimitive(trend);
            //   }
            // }

          });
        }
      }

      // if (wave.direction === 'down') {
      //   if (showLows && usedWave?.closestLows?.length) {
      //     usedWave.closestLows.forEach((level, i) => {
      //       const isTimeActive = level.start + windowTimeInCandles * timeMult < wave.end
      //       if (!isTimeActive) return

      //       if (showLevels) {
      //         const point1 = { time: level.end, price: level.low }
      //         const point2 = { time: nextWave.end, price: level.low }
      //         const trend = new TrendLine(chart, series, point1, point2, {
      //           lineColor: 'rgba(255, 255, 255, 0.99)',
      //           width: 1,
      //           showLabels: false,
      //         });
      //         series.attachPrimitive(trend);
      //       }

      //       // const bounceSLPercentShort = usedWave?.bounceSLPercentShort?.optimal?.limit
      //       // if (showSL && bounceSLPercentShort) {
      //       //   const sl = level.low - level.low * bounceSLPercentShort
      //       //   const point1 = { time: level.end, price: sl }
      //       //   const point2 = { time: nextWave.end, price: sl }
      //       //   const trend = new TrendLine(chart, series, point1, point2, {
      //       //     lineColor: 'rgba(255, 0, 0, 0.99)',
      //       //     width: 1,
      //       //     showLabels: false,
      //       //   });
      //       //   series.attachPrimitive(trend);
      //       // }

      //       // const bounceEnterShort = usedWave?.bounceEnterShort?.optimal?.limit
      //       // if (showEnterStart && bounceEnterShort) {
      //       //   const enter = level.low + level.low * bounceEnterShort
      //       //   const point1 = { time: level.end, price: enter }
      //       //   const point2 = { time: nextWave.end, price: enter }
      //       //   const trend = new TrendLine(chart, series, point1, point2, {
      //       //     lineColor: 'rgba(255, 255, 0, 0.99)',
      //       //     width: 1,
      //       //     showLabels: false,
      //       //   });
      //       //   series.attachPrimitive(trend);
      //       // }

      //       // const waveTargetPercent = usedWave?.waveTargetPercent
      //       // if (showTPPercent && waveTargetPercent) {
      //       //   const price = level.low + level.low * waveTargetPercent
      //       //   const point1 = { time: level.end, price }
      //       //   const point2 = { time: nextWave.end, price }
      //       //   const trend = new TrendLine(chart, series, point1, point2, {
      //       //     lineColor: 'rgba(155, 255, 155, 0.99)',
      //       //     width: 1,
      //       //     showLabels: false,
      //       //   });
      //       //   series.attachPrimitive(trend);
      //       // }

      //       // const waveTargetDelta = wave.waveTargetDeltaDown
      //       // if (showTPRatio && waveTargetDelta) {
      //       //   const price = level.low + waveTargetDelta
      //       //   const point1 = { time: level.end, price }
      //       //   const point2 = { time: nextWave.end, price }
      //       //   const trend = new TrendLine(chart, series, point1, point2, {
      //       //     lineColor: 'rgba(15, 255, 205, 0.99)',
      //       //     width: 1,
      //       //     showLabels: false,
      //       //   });
      //       //   series.attachPrimitive(trend);
      //       // }

      //       // const clusterTargetsDown = wave.clusterTargetsDown
      //       // if (showTPRatioClusters && clusterTargetsDown.length) {
      //       //   for (const target of clusterTargetsDown) {
      //       //     const price = level.low + target
      //       //     const point1 = { time: level.end, price }
      //       //     const point2 = { time: nextWave.end, price }
      //       //     const trend = new TrendLine(chart, series, point1, point2, {
      //       //       lineColor: 'rgba(15, 205, 240, 0.8)',
      //       //       width: 1,
      //       //       showLabels: false,
      //       //     });
      //       //     series.attachPrimitive(trend);
      //       //   }
      //       // }
      //     });
      //   }
      // }
    }


  }


  try {
    series.setData(seriesData);
  } catch (error) {
    console.log('series.setData drawWaves', error)
  }

  return series
}

export function getBacktestTradesSeries(chart) {
  const series = []


  const buySeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `BUY Long`,
  });
  series.push(buySeriesLong)

  const sellSeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `SELL Long`,
  });
  series.push(sellSeriesLong)

  const buySeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `BUY`,
  });
  series.push(buySeriesShort)

  const sellSeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `SELL`,
  });
  series.push(sellSeriesShort)

  const buySeriesDataLong = [];
  const sellSeriesDataLong = [];
  const buySeriesDataShort = [];
  const sellSeriesDataShort = [];

  return {
    series,
    buySeriesLong,
    sellSeriesLong,
    buySeriesShort,
    sellSeriesShort,
    buySeriesDataLong,
    sellSeriesDataLong,
    buySeriesDataShort,
    sellSeriesDataShort,
  }
}

export function drawBacktestTradesVertical(chart, opts, series2) {
  const { entryPoints, exitPointsShort, exitPointsLong } = opts

  if (!entryPoints?.bounce?.long?.length && !entryPoints?.bounce?.short?.length && !exitPointsLong?.length && !exitPointsShort?.length) return null

  // console.log('drawBacktestTrades', entryPoints?.bounce?.short, exitPointsShort, entryPoints?.bounce?.long, exitPointsLong)

  const series = getBacktestTradesSeries(chart)
  const {
    buySeriesLong,
    sellSeriesLong,
    buySeriesShort,
    sellSeriesShort,
    buySeriesDataLong,
    sellSeriesDataLong,
    buySeriesDataShort,
    sellSeriesDataShort,
  } = series

  try {


    for (const point of entryPoints.bounce.long) {
      series.buySeriesDataLong.push(point);
      const color = point.isFirstPoint ? 'rgba(0,255,255,0.99)' : 'rgba(0,255,0,0.3)'

      const vertLine = new VertLine(chart, series.buySeriesLong, point.time, {
        labelText: 'Enter BLong',
        width: 1,
        showLabel: point.isFirstPoint,
        color,
        labelTextColor: 'white',
        labelBackgroundColor: 'rgba(0,255,0,0.3)',
      });
      series.buySeriesLong.attachPrimitive(vertLine);

      // const point1 = { time: point.time, price: point.value }
      // const point2 = { time: point.time, price: point.value, label: `${point.value}` }
      // const trend = new TrendLine(chart, series.buySeriesLong, point1, point2, {
      //   lineColor: 'rgba(0,255,0,0.3)',
      //   width: 1,
      //   showLabels: false,
      //   labelBackgroundColor: "rgba(255, 255, 255, 0)",
      //   labelTextColor: 'rgba(0,255,0,0.3)'
      // });
      // series.buySeriesLong.attachPrimitive(trend);

    }
    buySeriesLong.setData(buySeriesDataLong);

    for (const point of exitPointsLong) {
      series.sellSeriesDataLong.push(point);

      const color = point.closed ? 'rgba(255,0,255,0.99)' : 'rgba(255,0,0,0.3)'

      const vertLine = new VertLine(chart, series.sellSeriesLong, point.time, {
        labelText: 'Exit Long',
        width: 1,
        showLabel: point.closed,
        color,
        labelTextColor: 'white',
        labelBackgroundColor: 'rgba(255,0,0,0.3)',
      });
      series.sellSeriesLong.attachPrimitive(vertLine);
    }
    sellSeriesLong.setData(sellSeriesDataLong);


    for (const point of entryPoints.bounce.short) {
      sellSeriesDataShort.push(point);

      const vertLine = new VertLine(chart, sellSeriesShort, point.time, {
        // labelText: ev.type,
        width: 1,
        // showLabel: true,
        color: 'rgba(255,0,0,0.3)',
        // labelTextColor: 'white',
        // labelBackgroundColor: 'rgba(255,0,0,0.3)',
      });
      sellSeriesShort.attachPrimitive(vertLine);
    }
    sellSeriesShort.setData(sellSeriesDataShort);

    for (const point of exitPointsShort) {
      buySeriesDataShort.push(point);

      const vertLine = new VertLine(chart, buySeriesShort, point.time, {
        // labelText: ev.type,
        width: 1,
        // showLabel: true,
        color: 'rgba(0,255,0,0.3)',
        // labelTextColor: 'white',
        // labelBackgroundColor: 'rgba(0,255,0,0.3)',
      });
      buySeriesShort.attachPrimitive(vertLine);
    }
    buySeriesShort.setData(buySeriesDataShort);

  } catch (error) {

  }

  return series
}

export function getBacktestTradesSeriesHorizontal(chart) {
  const series = []


  const buySeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `BUY Long`,
  });
  series.push(buySeriesLong)

  const sellSeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `SELL Long`,
  });
  series.push(sellSeriesLong)

  const buySeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `BUY`,
  });
  series.push(buySeriesShort)

  const sellSeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `SELL`,
  });
  series.push(sellSeriesShort)

  const buySeriesDataLong = [];
  const sellSeriesDataLong = [];
  const buySeriesDataShort = [];
  const sellSeriesDataShort = [];

  return {
    series,
    buySeriesLong,
    sellSeriesLong,
    buySeriesShort,
    sellSeriesShort,
    buySeriesDataLong,
    sellSeriesDataLong,
    buySeriesDataShort,
    sellSeriesDataShort,
  }
}

export function drawBacktestTradesHorizontal(chart, opts, series2) {
  const { state, allCandles } = opts
  const { actionsLong, actionsShort } = state

  if (!actionsLong?.length && !actionsShort?.length) return null

  const series = []

  // console.log('drawBacktestTradesHorizontal', state)
  drawActions(chart, actionsLong, 'long', opts, series)
  drawActions(chart, actionsShort, 'short', opts, series)


  return series
}


function drawActions(chart, actions, direction, opts, series) {
  const { state, allCandles } = opts

  const showTargets = false

  for (let i = 0; i < actions?.length || 0; i++) {
    const trade = actions[i]
    const enters = trade.entries || [];
    const exits = trade.exits || [];
    const labelIndex = i // + 1

    const avgEnter = enters.reduce((acc, point) => acc + point.value, 0) / enters.length || 0
    const avgExit = exits.reduce((acc, point) => acc + point.value, 0) / exits.length || 0

    const profitColor = 'rgba(98, 255, 0, 0.9)'
    const tpColor = 'rgba(0,255,0,0.9)'
    const lossColor = 'rgba(255, 0, 81, 0.9)'
    const slColor = 'rgba(255,0,0,0.9)'

    const longColor = avgEnter * 1.001 < avgExit ? profitColor : lossColor
    const shortColor = avgEnter > avgExit * 1.001 ? profitColor : lossColor

    const directionColor = direction === 'long' ? longColor : shortColor
    const directionEnterLabel = direction === 'long' ? 'LB' : 'SS'
    const directionExitLabel = direction === 'long' ? 'LS' : 'SB'


    // const maxLines = Math.max(enters.length, exits.length)
    const currentSeries = []

    let lastEnter = { time: 0, value: 0 }
    let lastExit = { time: 0, value: 0 }

    for (let j = 0; j < enters.length; j++) {
      const currentEnter = enters[j]

      if (currentEnter.time === lastEnter.time || !currentSeries.length) {

      }

      let hasExit = false
      for (let k = 0; k < exits.length; k++) {
        hasExit = true
        const currentExit = exits[k]

        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.value },
          { time: currentExit.time, value: currentExit.value },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const isSL = currentExit.reason === 'SL' || currentExit.reason === 'TSL'
        const label = j === 0 && k === 0 ? `${directionEnterLabel}-${labelIndex}` : undefined
        const label2 = isSL ? directionExitLabel : undefined
        const point1 = { time: currentEnter.time, price: currentEnter.value, label }
        const point2 = { time: currentExit.time, price: currentExit.value, label: label2 }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: directionColor,
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: directionColor
        });
        lineSeries.attachPrimitive(trend);

        if (j === 0 && k === 0) {
          const vertLine = new VertLine(chart, lineSeries, currentEnter.time, {
            labelText: `${directionEnterLabel}-${labelIndex}`,
            width: 1,
            showLabel: true,
            color: directionColor,
            labelTextColor: 'white',
            labelBackgroundColor: directionColor,
          });
          lineSeries.attachPrimitive(vertLine);
        }

      }

      if (!hasExit) {
        const currentExit = {
          time: allCandles[allCandles.length - 1].time,
          value: allCandles[allCandles.length - 1].close,
          reason: 'PNL',
        }

        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.value },
          { time: currentExit.time, value: currentExit.value },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const label = `LB-${labelIndex}`
        const label2 = currentExit.reason === 'SL' ? directionExitLabel : undefined
        const point1 = { time: currentEnter.time, price: currentEnter.value, label }
        const point2 = { time: currentExit.time, price: currentExit.value, label: label2 }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: directionColor,
          width: 4,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: directionColor
        });
        lineSeries.attachPrimitive(trend);

        const vertLine = new VertLine(chart, lineSeries, currentEnter.time, {
          labelText: `${directionEnterLabel}-${labelIndex}`,
          width: 1,
          showLabel: true,
          color: directionColor,
          labelTextColor: 'white',
          labelBackgroundColor: directionColor,
        });
        lineSeries.attachPrimitive(vertLine);

      }
    }

    const drawTradeLevels = true
    const showReverse = false

    if (drawTradeLevels) {
      const currentEnter = enters[0]
      let currentExit
      if (exits.length) {
        currentExit = exits[exits.length - 1]
      } else {
        currentExit = { time: allCandles[allCandles.length - 1].time, value: allCandles[allCandles.length - 1].close }
      }

      // *****************************************
      // LEVEL H1
      // *****************************************
      if (currentEnter.trade.levelH1) {
        const lineSeriesBase = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeriesBase)

        const dataBase = [
          { time: currentEnter.time, value: currentEnter.trade.levelH1 },
          { time: currentExit.time, value: currentEnter.trade.levelH1 },
        ]

        try {
          lineSeriesBase.setData(dataBase)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1b = { time: currentEnter.time, price: currentEnter.trade.levelH1 }
        const point2b = { time: currentExit.time, price: currentEnter.trade.levelH1, label: `H1` }

        const trendBase = new TrendLine(chart, lineSeriesBase, point1b, point2b, {
          lineColor: 'rgba(255, 255, 255, 0.9)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 255, 255, 0.9)'
        });
        lineSeriesBase.attachPrimitive(trendBase);
      }
      // *****************************************
      // LEVEL L1
      // *****************************************
      if (currentEnter.trade.levelL1) {
        const lineSeriesOpBase = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeriesOpBase)

        const dataOpBase = [
          { time: currentEnter.time, value: currentEnter.trade.levelL1 },
          { time: currentExit.time, value: currentEnter.trade.levelL1 },
        ]
        try {
          lineSeriesOpBase.setData(dataOpBase)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1ob = { time: currentEnter.time, price: currentEnter.trade.levelL1 }
        const point2ob = { time: currentExit.time, price: currentEnter.trade.levelL1, label: `L1` }

        const trendOpBase = new TrendLine(chart, lineSeriesOpBase, point1ob, point2ob, {
          lineColor: 'rgba(255, 255, 255, 0.9)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 255, 255, 0.9)'
        });
        lineSeriesOpBase.attachPrimitive(trendOpBase);
      }

      // *****************************************
      // Reverse LEVEL
      // *****************************************


      if (showReverse && currentEnter.trade.levelH1Rev) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.levelH1Rev },
          { time: currentExit.time, value: currentEnter.trade.levelH1Rev },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1 = { time: currentEnter.time, price: currentEnter.trade.levelH1Rev }
        const point2 = { time: currentExit.time, price: currentEnter.trade.levelH1Rev, label: `R_H1` }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(255, 255, 255, 0.9)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 255, 255, 0.9)'
        });
        lineSeries.attachPrimitive(trend);
      }

      // *****************************************
      // Reverse OPPOSITE LEVEL
      // *****************************************
      if (showReverse && currentEnter.trade.levelL1Rev) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.levelL1Rev },
          { time: currentExit.time, value: currentEnter.trade.levelL1Rev },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1 = { time: currentEnter.time, price: currentEnter.trade.levelL1Rev }
        const point2 = { time: currentExit.time, price: currentEnter.trade.levelL1Rev, label: `R_L1` }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(255, 255, 255, 0.9)',
          width: 2,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 255, 255, 0.9)'
        });
        lineSeries.attachPrimitive(trend);
      }

      if (showTargets && currentEnter.trade.pullbackTargetDeltaPrevWavePrice) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.pullbackTargetDeltaPrevWavePrice },
          { time: currentExit.time, value: currentEnter.trade.pullbackTargetDeltaPrevWavePrice },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1 = {
          time: currentEnter.time,
          price: currentEnter.trade.pullbackTargetDeltaPrevWavePrice
        }
        const point2 = {
          time: currentExit.time,
          price: currentEnter.trade.pullbackTargetDeltaPrevWavePrice,
          label: `ptdpw`
        }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(0, 200, 255, 0.99)',
          width: 3,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(0, 200, 255, 0.99)'
        });
        lineSeries.attachPrimitive(trend);
      }

      if (showTargets && currentEnter.trade.moveTargetDeltaPrevWavePrice) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.moveTargetDeltaPrevWavePrice },
          { time: currentExit.time, value: currentEnter.trade.moveTargetDeltaPrevWavePrice },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1 = {
          time: currentEnter.time,
          price: currentEnter.trade.moveTargetDeltaPrevWavePrice
        }
        const point2 = {
          time: currentExit.time,
          price: currentEnter.trade.moveTargetDeltaPrevWavePrice,
          label: `mtdpw`
        }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(255, 0, 255, 0.99)',
          width: 3,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 0, 255, 0.99)'
        });
        lineSeries.attachPrimitive(trend);
      }

      if (showTargets && currentEnter.trade.pullbackTargetDeltaLastWaveUpdatedPrice) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.pullbackTargetDeltaLastWaveUpdatedPrice },
          { time: currentExit.time, value: currentEnter.trade.pullbackTargetDeltaLastWaveUpdatedPrice },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }
        const point1 = {
          time: currentEnter.time,
          price: currentEnter.trade.pullbackTargetDeltaLastWaveUpdatedPrice
        }
        const point2 = {
          time: currentExit.time,
          price: currentEnter.trade.pullbackTargetDeltaLastWaveUpdatedPrice,
          label: `ptdlwu`
        }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(255, 132, 0, 0.99)',
          width: 3,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 132, 0, 0.99)'
        });
        lineSeries.attachPrimitive(trend);
      }

      if (showTargets && currentEnter.trade.moveTargetDeltaLastWaveUpdatedPrice) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: 'rgb(255, 255, 255)',
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal SL ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)

        const data = [
          { time: currentEnter.time, value: currentEnter.trade.moveTargetDeltaLastWaveUpdatedPrice },
          { time: currentExit.time, value: currentEnter.trade.moveTargetDeltaLastWaveUpdatedPrice },
        ]
        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('lineSeries.setData error', error)
        }

        const point1 = {
          time: currentEnter.time,
          price: currentEnter.trade.moveTargetDeltaLastWaveUpdatedPrice
        }
        const point2 = {
          time: currentExit.time,
          price: currentEnter.trade.moveTargetDeltaLastWaveUpdatedPrice,
          label: `mtdlwu`
        }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: 'rgba(255, 255, 0, 0.99)',
          width: 3,
          showLabels: false,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: 'rgba(255, 255, 0, 0.99)'
        });
        lineSeries.attachPrimitive(trend);
      }

      // *****************************************
      // STOP LOSS
      // *****************************************
      const lineSeries = chart.addSeries(LineSeries, {
        color: slColor,
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal SL ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeries)

      const data = [
        { time: currentEnter.time, value: currentEnter.trade.sl },
        { time: currentExit.time, value: currentEnter.trade.sl },
      ]
      try {
        lineSeries.setData(data)
      } catch (error) {
        console.log('lineSeries.setData error', error)
      }

      const point1 = { time: currentEnter.time, price: currentEnter.trade.sl }
      const point2 = { time: currentExit.time, price: currentEnter.trade.sl, label: `SL` }

      const trend = new TrendLine(chart, lineSeries, point1, point2, {
        lineColor: slColor,
        width: 1,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: slColor
      });
      lineSeries.attachPrimitive(trend);

      const sls = currentEnter.trade.sls
      for (let t = 0; t < sls.length; t++) {
        const currentSL = sls[t]
        if (!currentSL.price) {
          console.log('currentSL invalid', currentSL)
          continue
        }

        const point1 = { time: currentEnter.time, price: currentSL.price }
        const point2 = { time: currentExit.time, price: currentSL.price, label: `SL-${currentSL.label}` }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: slColor,
          width: 1,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: slColor
        });
        lineSeries.attachPrimitive(trend);
      }

      // *****************************************
      // TAKE PROFITS
      // *****************************************
      for (let t = 0; t < currentEnter.trade.tps.length; t++) {
        const tp = currentEnter.trade.tps[t]
        const lineSeries = chart.addSeries(LineSeries, {
          color: tpColor,
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          lastValueVisible: false,
          priceLineVisible: false,
          axisLabelVisible: false,
          // title: `TradesHorizontal TP ${i}-${j}-${k}`,
        });
        currentSeries.push(lineSeries)


        const data = [
          { time: currentEnter.time, value: tp },
          { time: currentExit.time, value: tp },
        ]

        try {
          lineSeries.setData(data)
        } catch (error) {
          console.log('TAKE PROFITS error', error)
        }

        const point1 = { time: currentEnter.time, price: tp }
        const point2 = { time: currentExit.time, price: tp, label: `TP-${t}` }

        const trend = new TrendLine(chart, lineSeries, point1, point2, {
          lineColor: tpColor,
          width: 1,
          showLabels: true,
          labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
          labelTextColor: tpColor
        });
        lineSeries.attachPrimitive(trend);
      }
    }

    // if (enters[0].time !== exits[0].time) {
    // const lineSeries = chart.addSeries(LineSeries,{
    //   color: 'rgba(255, 255, 255, 0)',
    //   lineWidth: 1,
    //   lineStyle: LineStyle.Solid,
    //   lastValueVisible: false,
    //   priceLineVisible: false,
    //   axisLabelVisible: false,
    //   // title: `TradesHorizontal ${i}-${0}`,
    // });
    // currentSeries.push(lineSeries)

    // const data = [
    //   { time: enters[0].time, value: enters[0].value },
    //   { time: exits[0].time, value: exits[0].value },
    // ]
    // currentSeries[0].setData(data)

    // const point1 = { time: enters[0].time, price: enters[0].value }
    // const point2 = { time: exits[0].time, price: exits[0].value, label: `${exits[0].value}` }

    // const trend = new TrendLine(chart, currentSeries[0], point1, point2, {
    //   lineColor: directionColor,
    //   width: 5,
    //   showLabels: false,
    //   labelBackgroundColor: "rgba(255, 255, 255, 0)",
    //   labelTextColor: directionColor
    // });
    // currentSeries[0].attachPrimitive(trend);

    // console.log('drawBacktestTradesHorizontal', data)
    // } else {
    //   console.log('enter and exit time is the same', enters[0].time, exits[0].time)
    // }



    // const lineSeries = chart.addSeries(LineSeries,{
    //   color: 'rgba(255, 255, 255, 0)',
    //   lineWidth: 1,
    //   lineStyle: LineStyle.Solid,
    //   lastValueVisible: false,
    //   priceLineVisible: false,
    //   axisLabelVisible: false,
    //   title: `TradesHorizontal ${i}-${j}`,
    // });
    // currentSeries.push(lineSeries)

    // currentSeries[0].setData(enters.map(point => ({ time: point.time, value: point.value })))

    // const point1 = { time: point.time, price: point.value }
    // const point2 = { time: point.time, price: point.value, label: `${point.value}` }
    // const trend = new TrendLine(chart, series.buySeriesLong, point1, point2, {
    //   lineColor: 'rgba(0,255,0,0.3)',
    //   width: 1,
    //   showLabels: false,
    //   labelBackgroundColor: "rgba(255, 255, 255, 0)",
    //   labelTextColor: 'rgba(0,255,0,0.3)'
    // });
    // series.buySeriesLong.attachPrimitive(trend);

    series.push(...currentSeries)
  }
}


export function drawBacktestDebug(chart, opts) {
  const { backtestResults, endTime, seriesMarkers } = opts
  const {
    wavesChanges, wavesNew,
    wavesLastWaveUpdate, wavesExteremUpdate,
    allCandles, state, stateInverse
  } = backtestResults

  const waves = wavesChanges

  const series = []

  // ******************************
  // CALCULATIONS
  // ******************************
  const times = []
  const pullbackTargetUp = []
  const pullbackTargetDown = []
  const moveTargetUp = []
  const moveTargetDown = []

  const closestHighsPrices = []
  const closestHighsPrices2 = []
  const closestLowsPrices = []


  for (let wave of waves) {
    const time = wave?.lastCandle?.time
    if (!time) continue
    times.push(time)

    const pullbackUp = wave.calc.pullbackTargetDeltaLastWaveUpdated.up || 0
    pullbackTargetUp.push(pullbackUp)

    const pullbackDown = wave.calc.pullbackTargetDeltaLastWaveUpdated.down || 0
    pullbackTargetDown.push(pullbackDown)

    const moveUp = wave.calc.moveTargetDeltaLastWaveUpdated.up || 0
    moveTargetUp.push(moveUp)

    const moveDown = wave.calc.moveTargetDeltaLastWaveUpdated.down || 0
    moveTargetDown.push(moveDown)

    // levels
    const upIndex = -1
    const closestHigh = wave.calc.closestHighsPrices[wave.calc.closestHighsPrices.length + upIndex]
      || closestHighsPrices[closestHighsPrices.length - 1] || 0
    closestHighsPrices.push(closestHigh)

    const upIndex2 = -2
    const closestHigh2 = wave.calc.closestHighsPrices[wave.calc.closestHighsPrices.length + upIndex2]
      || closestHighsPrices2[closestHighsPrices2.length - 1] || 0
    closestHighsPrices2.push(closestHigh2)

    const downIndex = -1
    const closestLow = wave.calc.closestLowsPrices[wave.calc.closestLowsPrices.length + downIndex]
      || closestLowsPrices[closestLowsPrices.length - 1] || 0
    closestLowsPrices.push(closestLow)
  }

  // ******************************
  // FILL DATA SERIES
  // ******************************

  const pullbackTargetUpData = [{ time: times[0], value: pullbackTargetUp[0] }]
  const pullbackTargetDownData = [{ time: times[0], value: pullbackTargetDown[0] }]
  const moveTargetUpData = [{ time: times[0], value: moveTargetUp[0] }]
  const moveTargetDownData = [{ time: times[0], value: moveTargetDown[0] }]

  const closestHighData = [{ time: times[0], value: closestHighsPrices[0] }]
  const closestHighData2 = [{ time: times[0], value: closestHighsPrices2[0] }]
  const closestLowData = [{ time: times[0], value: closestLowsPrices[0] }]


  // fix times
  for (let i = 1; i < times.length; i++) {
    const t1 = times[i]
    const t2 = times[i - 1]
    if (t1 === t2) {
      times[i]++
    }

    pullbackTargetUpData.push({ time: t1, value: pullbackTargetUp[i] })
    pullbackTargetDownData.push({ time: t1, value: pullbackTargetDown[i] })
    moveTargetUpData.push({ time: t1, value: moveTargetUp[i] })
    moveTargetDownData.push({ time: t1, value: moveTargetDown[i] })

    closestHighData.push({ time: t1, value: closestHighsPrices[i] })
    closestHighData2.push({ time: t1, value: closestHighsPrices2[i] })
    closestLowData.push({ time: t1, value: closestLowsPrices[i] })

  }

  // ******************************
  // DRAW SERIES
  // ******************************
  const seriesPullbackTargetUp = chart.addSeries(LineSeries, {
    color: 'rgb(150, 0, 0)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });

  series.push(seriesPullbackTargetUp)

  const seriesPullbackTargetDown = chart.addSeries(LineSeries, {
    color: 'rgb(0, 150, 0)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesPullbackTargetDown)

  const seriesMoveTargetUp = chart.addSeries(LineSeries, {
    color: 'rgb(150, 150, 0)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesMoveTargetUp)

  const seriesMoveTargetDown = chart.addSeries(LineSeries, {
    color: 'rgb(0, 150, 150)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesMoveTargetDown)


  const seriesClosestHigh = chart.addSeries(LineSeries, {
    color: 'rgb(255, 199, 199)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesClosestHigh)

  const seriesClosestHigh2 = chart.addSeries(LineSeries, {
    color: 'rgb(251, 70, 70)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesClosestHigh2)

  const seriesClosestLow = chart.addSeries(LineSeries, {
    color: 'rgb(179, 255, 216)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
  });
  series.push(seriesClosestLow)



  try {
    // seriesPullbackTargetUp.setData(pullbackTargetUpData);
    // seriesPullbackTargetDown.setData(pullbackTargetDownData);
    // seriesMoveTargetUp.setData(moveTargetUpData);
    // seriesMoveTargetDown.setData(moveTargetDownData);
    seriesClosestHigh.setData(closestHighData);
    seriesClosestHigh2.setData(closestHighData2);
    seriesClosestLow.setData(closestLowData);
  } catch (error) {
    console.log('series.setData error drawBacktestDebug', error)
  }


  return series
}

export function drawBacktestWaveMarkers(chart, opts) {
  const { backtestResults, seriesMarkers } = opts
  const { wavesChanges: waves } = backtestResults
  // wavesChanges, wavesNew, wavesLastWaveUpdate, wavesExteremUpdate,

  const markerTypes = [];
  const times = []

  for (let wave of waves) {
    const time = wave?.detectTime //wave?.prevCandle?.time
    if (!time) continue

    times.push(time)

    if (wave.newWave) {
      if (wave.undetected) {
        markerTypes.push(wave.type === 'pullback' ? 21 : 22);
      } else {
        markerTypes.push(wave.type === 'pullback' ? 11 : 12);
      }
    } else if (wave.extremUp) {
      if (wave.undetected) {
        markerTypes.push(wave.type === 'pullback' ? 41 : 42);
      } else {
        markerTypes.push(wave.type === 'pullback' ? 31 : 32);
      }
    } else {
      markerTypes.push(0);
    }
  }

  // ******************************
  // FILL DATA SERIES
  // ******************************
  const markers = [];
  for (let i = 0; i < times.length; i++) {
    const time = times[i]

    const shape = waves[i].direction === 'up' ? 'arrowDown' : 'arrowUp'
    const position = waves[i].direction === 'up' ? 'aboveBar' : 'belowBar'

    if (markerTypes[i] === 11 || markerTypes[i] === 12) {
      markers.push({
        time,
        position,
        color: 'rgb(255, 0, 230)',
        shape,
        text: markerTypes[i] === 11 ? `P` : 'M',
      })
    } else if (markerTypes[i] === 21 || markerTypes[i] === 22) {
      markers.push({
        time,
        position,
        color: 'rgba(255, 0, 144, 0.99)',
        shape,
        text: markerTypes[i] === 21 ? `p` : 'm',
      })
    } else if (markerTypes[i] === 31 || markerTypes[i] === 32) {
      markers.push({
        time,
        position,
        color: 'rgba(255, 255, 255, 0.8)',
        shape,
        text: markerTypes[i] === 32 ? `M` : undefined,
      })
    } else if (markerTypes[i] === 41 || markerTypes[i] === 42) {
      markers.push({
        time,
        position,
        color: 'rgba(92, 206, 255, 0.8)',
        shape,
        text: markerTypes[i] === 42 ? `m` : undefined,
      })
    }

  }


  seriesMarkers.setMarkers(markers);
}


export function getLevelColor(level) {
  switch (level) {
    case 'r3':
      return 'rgba(190, 90, 120, 0.7)'
    case 'r2':
      return 'rgba(190, 60, 120, 0.8)'
    case 'r1':
      return 'rgba(190, 30, 120, 0.9)'
    case 'pp':
      return 'rgba(30, 140, 10, 1)'
    case 's1':
      return 'rgba(90, 120, 190, 0.9)'
    case 's2':
      return 'rgba(90, 100, 190, 0.8)'
    case 's3':
      return 'rgba(90, 80, 190, 0.7)'
    default:
      return 'rgba(255, 255, 255, 0.5)'
  }
}

export function FindExtrems(candles, start, end) {
  let hh = -Infinity;
  let ll = Infinity;
  let hhIndex = -1;
  let llIndex = -1;
  let lows = [];
  let highs = [];

  for (let i = start; i < end; i++) {
    const candle = candles[i];

    if (candle.high > hh) {
      hh = candle.high;
      hhIndex = i;
      highs = [i];
    } else if (candle.high === hh) {
      highs.push(i);
    }

    if (candle.low < ll) {
      ll = candle.low;
      llIndex = i;
      lows = [i];
    } else if (candle.low === ll) {
      lows.push(i);
    }
  }
  return { hh, ll, hhIndex, llIndex, lows, highs };
}

export function ExtremLevels(candles, chart, series, maxDepth = 1) {
  // Инициализация данных серии
  const seriesData = [];
  const markers = [];

  for (let depth = 0; depth < maxDepth; depth++) {
    let startIndex = 0;
    let endIndex = candles.length;
    let extrems = FindExtrems(candles, startIndex, endIndex)
    let { hh, ll, hhIndex, llIndex, lows, highs } = extrems
    console.log('extrems', depth, extrems)
    const intervals = [startIndex, endIndex]

    if (hhIndex !== -1 && !intervals.includes(hhIndex)) {
      intervals.push(hhIndex)
      const candle = candles[hhIndex]
      markers.push({
        time: candle.time,
        position: 'aboveBar',
        color: '#841111',
        shape: 'arrowDown',
        text: `H:${depth}`,
      })
    }
    if (llIndex !== -1 && !intervals.includes(llIndex)) {
      intervals.push(llIndex)
      const candle = candles[llIndex]
      markers.push({
        time: candle.time,
        position: 'belowBar',
        color: '#118411',
        shape: 'arrowUp',
        text: `L:${depth}`,
      })
    }
    intervals.sort((a, b) => a - b)
    console.log('intervals', depth, intervals)
  }


  // seriesData.sort((a, b) => a.time - b.time);
  // // Отрисовываем серии на графике
  // const series = chart.addSeries(LineSeries,{
  //   color: 'red',
  //   lineWidth: 1,
  //   lineStyle: LineStyle.Solid,
  //   lastValueVisible: false,
  //   priceLineVisible: false,
  // });
  // series.setData(seriesData);
  series.setMarkers(markers);
}

export function getLatinLetterByIndex(index) {
  return String.fromCharCode(65 + index);
}

/**
 * ФИНАЛЬНАЯ ВЕРСИЯ.
 * Главная функция для детальной отладочной отрисовки.
 * Возвращает массив всех созданных серий для последующей очистки.
 * @param {object} chart - Экземпляр графика.
 * @param {object} opts - Объект с параметрами, содержащий portfolioManager и allCandles.
 * @returns {Array<object>} - Массив всех созданных на графике серий.
 */
export function drawPortfolioManagerTrades(chart, opts) {
  const { portfolioManager, allCandles } = opts;

  // Защита от невалидных данных
  if (!portfolioManager || (!portfolioManager.closedTrades?.length && !portfolioManager.activePositions?.length)) {
    return [];
  }

  const allCreatedSeries = [];
  let tradeIndex = 0;

  // Отрисовываем закрытые сделки
  for (const position of portfolioManager.closedTrades) {
    if (position.closeReason === 'Cancelled') continue;
    const tradeSeries = drawPositionAsActions(chart, position, tradeIndex, opts);
    allCreatedSeries.push(...tradeSeries);
    tradeIndex++;
  }
  // Отрисовываем активные сделки (если они остались в конце)
  for (const position of portfolioManager.activePositions) {
    const tradeSeries = drawPositionAsActions(chart, position, tradeIndex, opts);
    allCreatedSeries.push(...tradeSeries);
    tradeIndex++;
  }

  return allCreatedSeries;
}



function drawPositionAsActions(chart, position, tradeIndex, opts) {
  const { allCandles } = opts; // `state` больше не нужен

  // Данные берутся напрямую из объекта `position`
  const enters = position.entryOrders.filter(o => o.status === 'filled').map(o => ({
    time: o.executionTime,
    value: o.price,
    trade: { ...position.metadata, sl: position.initialStopLoss, tps: position.initialTakeProfits, sls: position.metadata.sls || [] }
  }));
  const exits = position.exitOrders.map(o => ({
    time: o.time,
    value: o.price,
    reason: o.reason
  }));
  const labelIndex = tradeIndex;

  const direction = position.direction
  // ...
  // Цвет определяется по PnL самой позиции

  //...
  const showTargets = false

  // for (let i = 0; i < actions?.length || 0; i++) {
  // const trade = actions[i]
  // const enters = trade.entries || [];
  // const exits = trade.exits || [];
  // const labelIndex = i // + 1

  const avgEnter = enters.reduce((acc, point) => acc + point.value, 0) / enters.length || 0
  const avgExit = exits.reduce((acc, point) => acc + point.value, 0) / exits.length || 0

  const profitColor = 'rgba(98, 255, 0, 0.9)'
  const tpColor = 'rgba(0,255,0,0.9)'
  const lossColor = 'rgba(255, 0, 81, 0.9)'
  const slColor = 'rgba(255,0,0,0.9)'

  const longColor = avgEnter * 1.001 < avgExit ? profitColor : lossColor
  const shortColor = avgEnter > avgExit * 1.001 ? profitColor : lossColor

  const directionColor = position.pnl >= 0 ? profitColor : lossColor;
  // const directionColor = direction === 'long' ? longColor : shortColor
  const directionEnterLabel = direction === 'long' ? 'LB' : 'SS'
  const directionExitLabel = direction === 'long' ? 'LS' : 'SB'


  // const maxLines = Math.max(enters.length, exits.length)
  const currentSeries = []

  let lastEnter = { time: 0, value: 0 }
  let lastExit = { time: 0, value: 0 }

  for (let j = 0; j < enters.length; j++) {
    const currentEnter = enters[j]

    let hasExit = false
    for (let k = 0; k < exits.length; k++) {
      hasExit = true
      const currentExit = exits[k]
      const t2 = currentEnter.time !== currentExit.time ? currentExit.time : currentExit.time + 60

      const lineSeries = chart.addSeries(LineSeries, {
        color: 'rgb(255, 255, 255)',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeries)

      const data = [
        { time: currentEnter.time, value: currentEnter.value },
        { time: t2, value: currentExit.value },
      ]

      try {
        lineSeries.setData(data)
      } catch (error) {
        console.log('lineSeries.setData error', error)
      }

      const isSL = currentExit.reason === 'SL' || currentExit.reason === 'TSL'
      const label = j === 0 && k === 0 ? `${directionEnterLabel}-${labelIndex}` : undefined
      const label2 = isSL ? directionExitLabel : undefined
      const point1 = { time: currentEnter.time, price: currentEnter.value, label }
      const point2 = { time: t2, price: currentExit.value, label: label2 }

      const trend = new TrendLine(chart, lineSeries, point1, point2, {
        lineColor: directionColor,
        width: 2,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: directionColor
      });
      lineSeries.attachPrimitive(trend);

      if (j === 0 && k === 0) {
        const vertLine = new VertLine(chart, lineSeries, currentEnter.time, {
          labelText: `${directionEnterLabel}-${labelIndex}`,
          width: 1,
          showLabel: true,
          color: directionColor,
          labelTextColor: 'white',
          labelBackgroundColor: directionColor,
        });
        lineSeries.attachPrimitive(vertLine);
      }

    }

    if (!hasExit) {
      const currentExit = {
        time: allCandles[allCandles.length - 1].time,
        value: allCandles[allCandles.length - 1].close,
        reason: 'PNL',
      }

      const lineSeries = chart.addSeries(LineSeries, {
        color: 'rgb(255, 255, 255)',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeries)

      const data = [
        { time: currentEnter.time, value: currentEnter.value },
        { time: currentExit.time, value: currentExit.value },
      ]
      try {
        lineSeries.setData(data)
      } catch (error) {
        console.log('lineSeries.setData error', error)
      }

      const label = `LB-${labelIndex}`
      const label2 = currentExit.reason === 'SL' ? directionExitLabel : undefined
      const point1 = { time: currentEnter.time, price: currentEnter.value, label }
      const point2 = { time: currentExit.time, price: currentExit.value, label: label2 }

      const trend = new TrendLine(chart, lineSeries, point1, point2, {
        lineColor: directionColor,
        width: 4,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: directionColor
      });
      lineSeries.attachPrimitive(trend);

      const vertLine = new VertLine(chart, lineSeries, currentEnter.time, {
        labelText: `${directionEnterLabel}-${labelIndex}`,
        width: 1,
        showLabel: true,
        color: directionColor,
        labelTextColor: 'white',
        labelBackgroundColor: directionColor,
      });
      lineSeries.attachPrimitive(vertLine);

    }
  }

  const drawTradeLevels = true
  const showReverse = false

  if (drawTradeLevels) {
    const currentEnter = enters[0]
    let currentExit
    if (exits.length) {
      currentExit = exits[exits.length - 1]
    } else {
      currentExit = {
        time: allCandles[allCandles.length - 1].time,
        value: allCandles[allCandles.length - 1].close
      }
    }
    const t2 = currentEnter.time !== currentExit.time ? currentExit.time : currentExit.time + 60


    // *****************************************
    // LEVEL H1
    // *****************************************
    if (currentEnter.trade.levelH1) {
      const lineSeriesBase = chart.addSeries(LineSeries, {
        color: 'rgb(255, 255, 255)',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal SL ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeriesBase)

      const dataBase = [
        { time: currentEnter.time, value: currentEnter.trade.levelH1 },
        { time: t2, value: currentEnter.trade.levelH1 },
      ]

      try {
        lineSeriesBase.setData(dataBase)
      } catch (error) {
        console.log('lineSeries.setData error', error)
      }

      const point1b = { time: currentEnter.time, price: currentEnter.trade.levelH1 }
      const point2b = { time: t2, price: currentEnter.trade.levelH1, label: `H1` }

      const trendBase = new TrendLine(chart, lineSeriesBase, point1b, point2b, {
        lineColor: 'rgba(255, 255, 255, 0.9)',
        width: 2,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: 'rgba(255, 255, 255, 0.9)'
      });
      lineSeriesBase.attachPrimitive(trendBase);
    }
    // *****************************************
    // LEVEL L1
    // *****************************************
    if (currentEnter.trade.levelL1) {
      const lineSeriesOpBase = chart.addSeries(LineSeries, {
        color: 'rgb(255, 255, 255)',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal SL ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeriesOpBase)

      const dataOpBase = [
        { time: currentEnter.time, value: currentEnter.trade.levelL1 },
        { time: t2, value: currentEnter.trade.levelL1 },
      ]
      try {
        lineSeriesOpBase.setData(dataOpBase)
      } catch (error) {
        console.log('lineSeries.setData error', error)
      }

      const point1ob = { time: currentEnter.time, price: currentEnter.trade.levelL1 }
      const point2ob = { time: t2, price: currentEnter.trade.levelL1, label: `L1` }

      const trendOpBase = new TrendLine(chart, lineSeriesOpBase, point1ob, point2ob, {
        lineColor: 'rgba(255, 255, 255, 0.9)',
        width: 2,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: 'rgba(255, 255, 255, 0.9)'
      });
      lineSeriesOpBase.attachPrimitive(trendOpBase);
    }

    // *****************************************
    // STOP LOSS
    // *****************************************
    const lineSeries = chart.addSeries(LineSeries, {
      color: slColor,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      lastValueVisible: false,
      priceLineVisible: false,
      axisLabelVisible: false,
      // title: `TradesHorizontal SL ${i}-${j}-${k}`,
    });
    currentSeries.push(lineSeries)

    const data = [
      { time: currentEnter.time, value: currentEnter.trade.sl },
      { time: t2, value: currentEnter.trade.sl },
    ]
    try {
      lineSeries.setData(data)
    } catch (error) {
      console.log('lineSeries.setData error', error)
    }

    const point1 = { time: currentEnter.time, price: currentEnter.trade.sl }
    const point2 = { time: t2, price: currentEnter.trade.sl, label: `SL` }

    const trend = new TrendLine(chart, lineSeries, point1, point2, {
      lineColor: slColor,
      width: 1,
      showLabels: true,
      labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
      labelTextColor: slColor
    });
    lineSeries.attachPrimitive(trend);

    const sls = currentEnter.trade.sls
    for (let t = 0; t < sls.length; t++) {
      const currentSL = sls[t]
      if (!currentSL.price) {
        console.log('currentSL invalid', currentSL)
        continue
      }

      const point1 = { time: currentEnter.time, price: currentSL.price }
      const point2 = { time: t2, price: currentSL.price, label: `SL-${currentSL.label}` }

      const trend = new TrendLine(chart, lineSeries, point1, point2, {
        lineColor: slColor,
        width: 1,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: slColor
      });
      lineSeries.attachPrimitive(trend);
    }

    // *****************************************
    // TAKE PROFITS
    // *****************************************
    for (let t = 0; t < currentEnter.trade.tps.length; t++) {
      const tp = currentEnter.trade.tps[t]
      const lineSeries = chart.addSeries(LineSeries, {
        color: tpColor,
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
        axisLabelVisible: false,
        // title: `TradesHorizontal TP ${i}-${j}-${k}`,
      });
      currentSeries.push(lineSeries)


      const data = [
        { time: currentEnter.time, value: tp },
        { time: t2, value: tp },
      ]

      try {
        lineSeries.setData(data)
      } catch (error) {
        console.log('TAKE PROFITS error', error)
      }

      const point1 = { time: currentEnter.time, price: tp }
      const point2 = { time: t2, price: tp, label: `TP-${t}` }

      const trend = new TrendLine(chart, lineSeries, point1, point2, {
        lineColor: tpColor,
        width: 1,
        showLabels: true,
        labelBackgroundColor: "rgba(0, 0, 0, 0.5)",
        labelTextColor: tpColor
      });
      lineSeries.attachPrimitive(trend);
    }
  }

  // if (enters[0].time !== exits[0].time) {
  // const lineSeries = chart.addSeries(LineSeries,{
  //   color: 'rgba(255, 255, 255, 0)',
  //   lineWidth: 1,
  //   lineStyle: LineStyle.Solid,
  //   lastValueVisible: false,
  //   priceLineVisible: false,
  //   axisLabelVisible: false,
  //   // title: `TradesHorizontal ${i}-${0}`,
  // });
  // currentSeries.push(lineSeries)

  // const data = [
  //   { time: enters[0].time, value: enters[0].value },
  //   { time: exits[0].time, value: exits[0].value },
  // ]
  // currentSeries[0].setData(data)

  // const point1 = { time: enters[0].time, price: enters[0].value }
  // const point2 = { time: exits[0].time, price: exits[0].value, label: `${exits[0].value}` }

  // const trend = new TrendLine(chart, currentSeries[0], point1, point2, {
  //   lineColor: directionColor,
  //   width: 5,
  //   showLabels: false,
  //   labelBackgroundColor: "rgba(255, 255, 255, 0)",
  //   labelTextColor: directionColor
  // });
  // currentSeries[0].attachPrimitive(trend);

  // console.log('drawBacktestTradesHorizontal', data)
  // } else {
  //   console.log('enter and exit time is the same', enters[0].time, exits[0].time)
  // }



  // const lineSeries = chart.addSeries(LineSeries,{
  //   color: 'rgba(255, 255, 255, 0)',
  //   lineWidth: 1,
  //   lineStyle: LineStyle.Solid,
  //   lastValueVisible: false,
  //   priceLineVisible: false,
  //   axisLabelVisible: false,
  //   title: `TradesHorizontal ${i}-${j}`,
  // });
  // currentSeries.push(lineSeries)

  // currentSeries[0].setData(enters.map(point => ({ time: point.time, value: point.value })))

  // const point1 = { time: point.time, price: point.value }
  // const point2 = { time: point.time, price: point.value, label: `${point.value}` }
  // const trend = new TrendLine(chart, series.buySeriesLong, point1, point2, {
  //   lineColor: 'rgba(0,255,0,0.3)',
  //   width: 1,
  //   showLabels: false,
  //   labelBackgroundColor: "rgba(255, 255, 255, 0)",
  //   labelTextColor: 'rgba(0,255,0,0.3)'
  // });
  // series.buySeriesLong.attachPrimitive(trend);

  return currentSeries
  // }
}